import { useState, useEffect, useMemo, useRef, type FormEvent, type ReactNode } from "react";
import { Trash2, Plus, Music, Star, X, Pencil, Check, ChevronDown } from "lucide-react";
import { ActionModal, AddButton, Button, Field, Textarea, Picker, EmptyState, RecordList, Spinner, Icon, IconButton, DragHandle, useDropTarget, Dropzone, Row, Rows, SectionScroller, SegmentedControl, Tag, type PickerOption, Checkbox, useMenuDismiss } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import {
  useShow,
  useCreateShow,
  useUpdateShow,
  useShowMembers,
  useTicketTiers,
  useReplaceTicketTiers,
  useParseRunOfShow,
  useInviteMembers,
  useSetMembers,
  useRunOfShow,
  useUpdateRunOfShow,
  useVip,
  useUpdateVip,
  useSetlist,
  useReplaceSetlist,
  useCopySetlistFromLast,
  useSongBank,
  useExpenses,
  useUpdateExpenses,
  useTours,
  useRuns,
  useCreateRun,
  useCreateTour,
  useTourDays,
  type TourDayDetail,
} from "../../app/hooks/useShows.js";
import { OffDayModal } from "./OffDayModal.js";
import { ExpenseLinesList, type ExpenseLineDraft } from "./ExpenseLinesList.js";
import {
  useFinancials, useUpdateFinancials, useCommissions, useUpdateCommissions,
  useSettlement, useUpdateSettlement, useShowLinkedDays, useSetMemberPay,
} from "../../app/hooks/useMoney.js";
import { useTravel, useParseItinerary, useSaveTravel, useSetTravelReimbursement } from "../../app/hooks/useTravel.js";
import { useArtistAutocomplete } from "../../app/hooks/useShowLookup.js";
import { useCapability, useBandContext } from "../../app/bandContext.js";
import { useUploadMedia, mediaKindForFile } from "../../app/hooks/useMedia.js";
import { useBandTeam } from "../../app/hooks/useBandTeam.js";
import { useInvitableRows } from "../../app/hooks/useAssignable.js";
import { RoleLabel } from "../contacts/RoleLabel.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { CrewInviteePicker } from "../dashboard/CrewInviteePicker.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { TypeaheadInput, type TypeaheadSuggestions } from "./TypeaheadInput.js";
import { useArtistSuggestions, useVenueSuggestions, venueMeta, type ActSuggestion, type VenueSuggestion } from "./showSuggestions.js";
import { useActPhotoSource, ActAvatar, isOwnAct } from "./actPhotos.js";
import { AcMark } from "./AcMark.js";
import { GoogleMapsAttribution, SpotifyArtistLink, SpotifyAttribution } from "../shared/VendorAttribution.js";
import { SharedBasicsView, SharedFlyerView, SharedLineupView, SharedRunOfShowView } from "./sharedShowSections.js";
import { useSetShareLink, type SharedShowDetail } from "../../app/hooks/useShowShares.js";
import { EditorFooterSlot } from "./EditorFooterSlot.js";
import { useSectionSave, useEnsureShow, usePendingInvites, useEditorRoster, useEditorRosterValue, useEditorTravel, useEditorTravelValue, SectionSave } from "./EditorSaveRegistry.js";
import { todayIso } from "./lib/bookingRows.js";
import { showBodyFrom } from "./lib/showBody.js";
import type { ShowCreateSeed } from "./showCreateSeed.js";
import { SLOT_ROLES } from "./sharedShowFormat.js";
import styles from "./ShowWizard.module.scss";
import { formatShowTime, isNextDay, parseLooseTime, parseWallClock, toWallClock } from "../../app/showNight.js";
import { applyCascade, buildDosPreview, setLengthEdited, setTimeEdited, type CascadeAct } from "./lib/runOfShow.js";
import { LineupDraftProvider, useLineupDraft, type DraftSlot } from "./lineupDraft.js";

type ShowDetail = components["schemas"]["ShowDetail"];
type LineupSlotDto = components["schemas"]["LineupSlotDto"];
type MemberDto = components["schemas"]["MemberDto"];
type SetlistItemDto = components["schemas"]["SetlistItemDto"];
type ExpenseLineDto = components["schemas"]["ExpenseLineDto"];

/** The 10 editor sections. All are URL-addressable (`?step=1-10`); wires 01 Basics +
 * 02 Lineup, the other 8 are shell placeholders their owning sessions fill (money sections are).*/
// VIP was removed from the editor for now (Ryan, ART-210); the sections after it renumber to stay contiguous.
export const SECTIONS = [
  { n: 1, key: "basics", label: "Basics" },
  { n: 2, key: "lineup", label: "Lineup" },
  { n: 3, key: "members", label: "Members" },
  { n: 4, key: "assets", label: "Assets" },
  { n: 5, key: "run-of-show", label: "Run of Show" },
  { n: 6, key: "setlist", label: "Setlist" },
  { n: 7, key: "travel", label: "Travel" },
  { n: 8, key: "financials", label: "Terms", money: true },
  { n: 9, key: "expenses", label: "Expenses" },
] as const;

/** 01 Basics' Show / Festival toggle. The wire values are the API's `kind` (`show` | `festival`). */
const KINDS: { value: string; label: string }[] = [
  { value: "show", label: "Show" },
  { value: "festival", label: "Festival" },
];

/** 01 Basics' Age restriction, the reference's option set verbatim. `age_restriction` is free text on the
 * wire, so the LABEL is the value — the guest's read-only basics view prints it straight back out. */
const AGE_RESTRICTIONS: PickerOption[] = [
  { value: "", label: "—" },
  { value: "All ages", label: "All ages" },
  { value: "16+", label: "16+" },
  { value: "18+", label: "18+" },
  { value: "21+", label: "21+" },
];

/** The Basics form's DOM id, so the footer's portaled Save can own it via `form=` — that also makes it the
 * form's default button again, keeping Enter-to-submit alive from any field. */
const BASICS_FORM_ID = "show-editor-basics";

export interface ShowWizardProps {
  /** Undefined = create mode (`/booking/shows/new`); set = edit mode. */
  showId?: string;
  /** Starting values for a CREATE — see {@link ShowCreateSeed}. Ignored once `showId` is set. */
  seed?: ShowCreateSeed;
  /** The active step (1-10) from `?step`; refresh/back land here. */
  step: number;
  onStep: (step: number) => void;
  onCreated: (showId: string) => void;
  onExit: () => void;
  /**
   * GUEST MODE (ART-165 shared shell). When set, this is a guest editing a shared show: the inviter-owned
   * sections (basics / lineup / run of show) render as DISABLED editor fields from this matrix (`Shared*View`),
   * and the guest's OWN per-band sections (members / setlist / vip / travel / financials / expenses) render the
   * SAME editable components the owner uses — they self-load through their now-guest-reachable endpoints and
   * register their own Save. The visible/editable section set is SERVER-owned (`guest.sections`). Only reached
   * by a `schedule:write` member (the view modal gates Edit), so there is no separate read-only mode.
   * `useShow` (the owner-fenced read) is not called in this mode.
   */
  guest?: SharedShowDetail;
}

/** The guest editor's read-only inviter sections vs its editable per-band sections (ART-165). The read-only
 *  three use the shared views (from the matrix); everything else is the real owner section component. */
const GUEST_SECTION_LABELS: Record<string, string> = {
  basics: "Basics", lineup: "Lineup", "run-of-show": "Run of Show",
  members: "Members", setlist: "Setlist", vip: "VIP", travel: "Travel",
  financials: "Terms", expenses: "Expenses",
};

/**
 * The show editor's BODY — a left section rail + the active section, every step deep-linkable and
 * refresh-safe (the step is `?step`, the show reloads from the server).
 *
 * The header, the footer ribbon and the overlay chrome belong to `ShowEditorModal`, which mounts this: the
 * editor is a popup over the Booking list, not a screen. Money-free by construction except section 09
 * Financials, which is its own capability-gated sub-component calling its own money endpoints.
 */
export function ShowWizard({ showId, seed, step, onStep, onCreated, onExit, guest }: ShowWizardProps) {
  // In guest mode the show is read through the matrix (passed in), never the owner-fenced `useShow` (which
  // 404s a guest) — disable the query by passing no id.
  const show = useShow(guest ? undefined : showId);
  const { bandId } = useBandContext();

  const editing = !!showId;
  const loading = !guest && editing && show.isLoading;
  const errored = !guest && editing && show.isError;
  const detail = show.data?.data;
  const version = show.data?.version ?? 0;

  /** One guest section's body: the inviter's three render as DISABLED editor fields (from the matrix, via the
   *  shared `Shared*View` — the same fields the owner edits, disabled, per point 3), and the guest's own
   *  sections render the real owner components editable (they self-load through the guest-reachable endpoints).
   *  Only a `schedule:write` member reaches this editor, so the own sections are always editable here. */
  function guestSectionBody(key: string) {
    switch (key) {
      // ART-300 — Basics stays the inviter's (read-only) EXCEPT the run/tour link: each band links a shared date
      // into its OWN run or tour, so the guest's picker is live and lists the guest band's runs and tours.
      case "basics": return <div className={styles.section}><SharedBasicsView detail={guest!} /><GuestRunTourLink detail={guest!} /><SharedFlyerView detail={guest!} /></div>;
      case "lineup": return <div className={styles.section}><SharedLineupView detail={guest!} /></div>;
      case "run-of-show": return <div className={styles.section}><SharedRunOfShowView detail={guest!} /></div>;
      case "members": return <MembersSection showId={showId} />;
      case "setlist": return <SetlistSection showId={showId} />;
      case "vip": return <VipSection showId={showId} />;
      case "travel": return <TravelSection showId={showId} />;
      case "financials": return <FinancialsSection showId={showId} seedGuaranteeCents={null} seedDoorSplitPct={null} />;
      case "expenses": return <CustomExpensesSection showId={showId} />;
      default: return null;
    }
  }

  /** One section's body. Every section renders whether or not the show exists yet — they hold their edits
   *  locally and the footer's Save creates the record then flushes them (`EditorSaveRegistry`). */
  function sectionBody(key: string) {
    switch (key) {
      case "basics": return <BasicsSection showId={showId} seed={seed} detail={detail} version={version} onCreated={onCreated} onExit={onExit} />;
      // The lineup draft (server slots, our own chip, ownerBandId) lives in `LineupDraftProvider` above, so
      // the section reads it from there and Run of Show sees the same live bill.
      case "lineup": return <LineupSection showId={showId} />;
      case "members": return <MembersSection showId={showId} />;
      case "assets": return <AssetsSection showId={showId} detail={detail} version={version} />;
      case "run-of-show": return <RunOfShowSection showId={showId} />;
      case "setlist": return <SetlistSection showId={showId} />;
      case "vip": return <VipSection showId={showId} />;
      case "travel": return <TravelSection showId={showId} />;
      case "financials": return (
        <FinancialsSection
          showId={showId}
          seedGuaranteeCents={showId ? null : seed?.guaranteeCents ?? null}
          seedDoorSplitPct={showId ? null : seed?.doorSplitPct ?? null}
        />
      );
      case "expenses": return <CustomExpensesSection showId={showId} />;
      default: return <PlaceholderSection label="This section" money={false} />;
    }
  }

  return (
    <div className={styles.wizard} data-testid="show-wizard">
      {loading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : errored ? (
        <EmptyState
          title="Couldn't load this show"
          description={show.error instanceof ApiError ? show.error.message : "Please try again."}
          action={<Button onClick={() => void show.refetch()}>Retry</Button>}
        />
      ) : (
        // ONE scroll holding all sections, with the rail as a sticky table of contents. `?step` is a scroll
        // TARGET here, not a switcher — refresh, back and deep-links all still land correctly. In guest mode
        // the rail + bodies come from the server-owned section set.
        guest ? (
          <>
            {/* The show already exists for a guest, so the save fan-out's ensure-show step just returns its id —
                the create step BasicsSection registers in owner mode, which never mounts here. Without it,
                saveAll aborts with "Basics didn't save" before it reaches the guest's own editable sections. */}
            {showId && <GuestEnsureShow showId={showId} />}
            <SectionScroller
              sections={(guest.sections ?? []).map((k, i) => ({ n: i + 1, label: GUEST_SECTION_LABELS[k] ?? k }))}
              scrollTo={step}
              onSelect={onStep}
              railLabel="Show sections"
              anchorPrefix="guest-sec"
              railHint="The inviter owns basics, lineup and run of show — you edit your own sections."
            >
              {(s) => { const k = (guest.sections ?? [])[s.n - 1]; return k ? guestSectionBody(k) : null; }}
            </SectionScroller>
          </>
        ) : (
          // ONE live lineup draft, shared by section 02 (Lineup) and section 05 (Run of Show) — an act added
          // in the former shows up immediately in the latter, and its set time saves in the same footer Save
          // (ART-209). Owner-only: guest mode renders both sections read-only from the server-owned matrix.
          <LineupDraftProvider serverLineup={detail?.lineup} ownerBandId={detail?.bandId ?? bandId}>
            <SectionScroller
              sections={SECTIONS.map((s) => ({ n: s.n, label: s.label }))}
              scrollTo={step}
              onSelect={onStep}
              railLabel="Editor sections"
              anchorPrefix="editor-sec"
            >
              {(s) => sectionBody(SECTIONS.find((x) => x.n === s.n)!.key)}
            </SectionScroller>
          </LineupDraftProvider>
        )
      )}
    </div>
  );
}


/** The phone editor's four tabs (ART-358) — the sections a booking agent owns. */
export type MobileEditorTab = "basics" | "lineup" | "assets" | "terms";

/**
 * The phone show editor's BODY (ART-358): the same Basics, Lineup, Assets and Terms sections the desktop editor
 * mounts, in their phone layouts. All four stay MOUNTED and only the active one shows, so an edit on one tab
 * survives a switch to another and the one Save (the caller's, over `EditorSaveProvider`) commits them all.
 */
export function MobileEditorSections({ showId, tab, onExit }: { showId: string; tab: MobileEditorTab; onExit: () => void }) {
  const show = useShow(showId);
  const { bandId } = useBandContext();
  const detail = show.data?.data;
  const version = show.data?.version ?? 0;
  if (show.isLoading) return <div className={styles.center}><Spinner /></div>;
  if (show.isError || !detail) {
    return (
      <EmptyState
        title="Couldn't load this show"
        description={show.error instanceof ApiError ? show.error.message : "Please try again."}
        action={<Button onClick={() => void show.refetch()}>Retry</Button>}
      />
    );
  }
  return (
    <LineupDraftProvider serverLineup={detail.lineup} ownerBandId={detail.bandId ?? bandId}>
      <div hidden={tab !== "basics"}>
        <BasicsSection mobile showId={showId} detail={detail} version={version} onCreated={() => {}} onExit={onExit} />
      </div>
      <div hidden={tab !== "lineup"}><LineupSection mobile showId={showId} /></div>
      <div hidden={tab !== "assets"}><AssetsSection mobile showId={showId} detail={detail} version={version} /></div>
      <div hidden={tab !== "terms"}><FinancialsSection mobile showId={showId} /></div>
    </LineupDraftProvider>
  );
}

/**
 * Guest-mode ensure-show (ART-165). A guest only ever edits a show that ALREADY exists, so the save fan-out's
 * create-or-return-id step is a no-op that hands back the known id. In owner mode `BasicsSection` registers this
 * (it may need to POST the show first); in guest mode `BasicsSection` never mounts, and without this `saveAll`
 * returns `failures: ["Basics"]` immediately — surfacing as "Basics didn't save" on every guest save. Renders
 * nothing.
 */
function GuestEnsureShow({ showId }: { showId: string }) {
  useEnsureShow(() => Promise.resolve(showId));
  return null;
}

// ---- Section 01: Basics ----------------------------------------------------------------------------------

/** A `YYYY-MM-DD` → a local Date (parts, never `new Date(iso)` which is UTC and can shift the day). */
function parseLocalDate(iso: string): Date | null {
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}
/** "Sat 11/21" — the reference's short weekday + m/d for a run's shows. */
function shortDow(iso: string): string {
  const dt = parseLocalDate(iso);
  if (!dt) return "";
  return `${dt.toLocaleDateString(undefined, { weekday: "short" })} ${dt.getMonth() + 1}/${dt.getDate()}`;
}
/** "Nov 20" — for the "Runs near {date}" section heading. */
function monthDay(iso: string): string {
  const dt = parseLocalDate(iso);
  return dt ? dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
}
/** "$100" — whole-dollar money for the day-cost sub-line (the reference drops cents there). */
function compactUsd(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

type RunLinkOptionRow = components["schemas"]["RunLinkOption"];
type TourSummaryRow = components["schemas"]["TourSummary"];

/** The combined "Link to run or tour" options, as the reference's rich two-line rows: "Not linked · one-off",
 *  then the band's runs (each with its show count + the OTHER shows it shares) under a "Runs near {date}"
 *  heading, "+ Start a run from this show", then tours and "+ Start a tour". Shared by the owner's Basics and the
 *  guest's link (ART-300) — both list the MOUNTED band's own runs and tours. A tour holding no shows is left out
 *  (PO: "if no shows, tour disappears"), unless it is the `current` link — a tour just started here has none yet. */
function buildRunTourLinkOptions(
  runs: readonly RunLinkOptionRow[] | null | undefined,
  tours: readonly TourSummaryRow[] | null | undefined,
  showId: string | undefined,
  date: string | undefined,
  current: string,
): PickerOption[] {
  const runGroup = date ? `Runs near ${monthDay(date)}` : "Runs";
  const runItems = (runs ?? []).filter((r) => !!r.runId);
  return [
    { value: "", label: "Not linked · one-off", render: <LinkOptRow title="Not linked · one-off" sub="This show only" /> },
    ...runItems.map((r) => {
      const count = r.showCount ?? (r.shows?.length ?? 0);
      const label = `${r.name ?? "Run"}${count > 0 ? ` · ${count} show${count === 1 ? "" : "s"}` : ""}`;
      const others = (r.shows ?? []).filter((s) => s.showId !== showId && s.date);
      const sub = others.length > 0
        ? "With " + others.map((s) => `${shortDow(s.date!)}${s.venueName ? ` · ${s.venueName}` : ""}`).join(", ")
        : (r.name ?? undefined);
      return { value: `run:${r.runId}`, label, group: runGroup, render: <LinkOptRow title={label} sub={sub} /> };
    }),
    {
      value: "new-run",
      label: "+ Start a run from this show",
      group: runGroup,
      render: <LinkOptRow title="+ Start a run from this show" sub="Link holds on the way there and back" />,
    },
    ...(tours ?? [])
      .filter((t) => !!t.tourId && ((t.showCount ?? 0) > 0 || `tour:${t.tourId}` === current))
      .map((t) => {
        const count = t.showCount ?? 0;
        const sub = count > 0 ? `Tour · ${count} show${count === 1 ? "" : "s"}` : "Tour";
        return { value: `tour:${t.tourId}`, label: t.name ?? "Tour", group: "Tours", render: <LinkOptRow title={t.name ?? "Tour"} sub={sub} /> };
      }),
    {
      value: "new-tour",
      label: "+ Start a tour",
      group: "Tours",
      render: <LinkOptRow title="+ Start a tour" sub="Name it, and this show is its first date" />,
    },
  ];
}

/** "+ Start a tour" from the link picker: a tour needs a name, so it is asked for here, then the new tour is
 *  selected. The show joins it when the editor saves. */
function StartTourDialog({ onCancel, onCreated }: { onCancel: () => void; onCreated: (tourId: string) => void }) {
  const createTour = useCreateTour();
  const [name, setName] = useState("");
  const trimmed = name.trim();
  function submit() {
    if (!trimmed) return;
    void createTour.mutateAsync({ name: trimmed, days: [] }).then((tour) => {
      if (tour.tourId) onCreated(tour.tourId);
    });
  }
  return (
    <ActionModal
      title="Start a tour"
      confirmLabel="Start tour"
      onConfirm={submit}
      onCancel={onCancel}
      loading={createTour.isPending}
      confirmDisabled={!trimmed}
    >
      <Field
        label="Tour name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
      />
    </ActionModal>
  );
}

/**
 * ART-300 — the GUEST's own "Link to run or tour" on a shared show. A support act on a friend's bill may be on a
 * tour of its own; the host's link lives on the host's show row (read-only and invisible here), so the guest's
 * lives on its own share row and lists the GUEST band's runs and tours. It saves through the footer's fan-out
 * like every other guest section (its own `PUT …/shares/{shareId}/link`), never touching the host's show. Only an
 * ACCEPTED share can be linked; a still-pending invite shows the control disabled.
 */
function GuestRunTourLink({ detail }: { detail: SharedShowDetail }) {
  const showId = detail.showId!;
  const tours = useTours();
  const runs = useRuns();
  const createRun = useCreateRun();
  const setShareLink = useSetShareLink(showId);
  const accepted = detail.status === "accepted";

  const stored = detail.runId ? `run:${detail.runId}` : detail.tourId ? `tour:${detail.tourId}` : "";
  // null = untouched (the picker shows the stored link, and a refetch after a save re-seeds it).
  const [draft, setDraft] = useState<string | null>(null);
  const link = draft ?? stored;
  const options = buildRunTourLinkOptions(runs.data?.items, tours.data?.items, showId, detail.date, link);
  const [startingTour, setStartingTour] = useState(false);

  function onChange(v: string) {
    if (v === "new-run") {
      void createRun.mutateAsync().then((run) => setDraft(`run:${run.runId}`));
      return;
    }
    if (v === "new-tour") { setStartingTour(true); return; }
    setDraft(v);
  }

  useSectionSave("guest-link", {
    label: "Basics",
    order: 1,
    isDirty: () => draft !== null && draft !== stored,
    save: async () => {
      const value = draft ?? stored;
      await setShareLink.mutateAsync({
        shareId: detail.shareId!,
        runId: value.startsWith("run:") ? value.slice("run:".length) : null,
        tourId: value.startsWith("tour:") ? value.slice("tour:".length) : null,
      });
      setDraft(null);
    },
  });

  return (
    <div className={styles.gridFull}>
      <Picker
        label="Link to run or tour"
        value={link}
        onChange={onChange}
        options={options}
        disabled={!accepted}
        triggerClassName={styles.linkTrigger}
        hint={accepted
          ? "Your own run or tour — the inviter never sees it. Linked shows share travel days, per diem and one member payout."
          : "Accept the invite to link this date into your own run or tour."}
      />
      {startingTour && (
        <StartTourDialog
          onCancel={() => setStartingTour(false)}
          onCreated={(tourId) => { setStartingTour(false); setDraft(`tour:${tourId}`); }}
        />
      )}
    </div>
  );
}

/** A two-line row for the "Link to run or tour" picker (bold title + grey sub). */
function LinkOptRow({ title, sub }: { title: string; sub?: string }) {
  return (
    <span className={styles.linkOpt}>
      <span className={styles.linkOptTitle}>{title}</span>
      {sub && <span className={styles.linkOptSub}>{sub}</span>}
    </span>
  );
}

function BasicsSection({
  showId,
  seed,
  detail,
  version,
  onCreated,
  onExit,
  mobile = false,
}: {
  showId?: string;
  seed?: ShowCreateSeed;
  detail?: ShowDetail;
  version: number;
  onCreated: (showId: string) => void;
  onExit: () => void;
  /** The phone editor's Basics (ART-358): the six fields an agent sets, paired to cut scrolling. The fields it
   *  hides keep their loaded values, so the full-replace PUT still carries them. */
  mobile?: boolean;
}) {
  const createShow = useCreateShow();
  const updateShow = useUpdateShow();
  // The reference's Tour field is free text whose VALUE is the grouping key. Our model has real tours, so the
  // same field is a picker over them — `buildTimeline` groups on the resolved name, which is the same rule.
  // ART-240 folds runs into that one "Link to run or tour" picker (a show links to a run OR a tour).
  const tours = useTours();
  const runs = useRuns();
  const createRun = useCreateRun();

  // Seeded only on a CREATE, and only as an initial value — see `ShowCreateSeed`. `detail` wins wherever it
  // exists, so an edit never sees a seed; today's date remains the unseeded default.
  const [date, setDate] = useState(detail?.date ?? seed?.date ?? todayIso());
  const [kind, setKind] = useState(
    (detail?.kind ?? seed?.kind) === "festival" ? "festival" : "show",
  );
  const [venueName, setVenueName] = useState(detail?.venueName ?? seed?.venueName ?? "");
  const [city, setCity] = useState(detail?.city ?? "");
  const [state, setState] = useState(detail?.state ?? "");
  const [country, setCountry] = useState(detail?.country ?? "");
  const [capacity, setCapacity] = useState(detail?.capacity != null ? String(detail.capacity) : "");
  const [ageRestriction, setAgeRestriction] = useState(detail?.ageRestriction ?? "");
  const [promoter, setPromoter] = useState(detail?.promoter ?? "");
  // ART-240 — one "Link to run or tour" value, a `run:{id}` / `tour:{id}` token (a show links to one or
  // neither). "+ New run" mints a run and selects it.
  const [link, setLink] = useState<string>(
    detail?.runId ? `run:${detail.runId}` : detail?.tourId ? `tour:${detail.tourId}` : "",
  );
  const [ticketUrl, setTicketUrl] = useState(detail?.ticketUrl ?? "");
  // The Google Places place id of the picked venue — the one Places fact a show may keep (Google exempts it from
  // its caching rules). Typing over a picked name unbinds it: the text no longer names that place.
  const [venuePlaceId, setVenuePlaceId] = useState(detail?.venuePlaceId ?? "");
  // The picked venue's coordinates. They place the DOS rideshare pin, and the server derives the show's timezone
  // from them (ART-128) — so they travel with the place id, bound by a pick and unbound with it.
  const [venueCoords, setVenueCoords] = useState<{ lat: number; lng: number } | null>(
    detail?.lat != null && detail?.lng != null ? { lat: detail.lat, lng: detail.lng } : null,
  );
  /** Typing over a picked name, or switching to a festival: the text no longer names that place. */
  function unbindVenue() {
    setVenuePlaceId("");
    setVenueCoords(null);
  }
  const [saved, setSaved] = useState(false);
  /** Basics holds plain field state rather than a null draft, so it tracks dirty explicitly. Every setter
   *  below routes through `touch` so a new field can never forget to mark the form. */
  const [basicsDirty, setBasicsDirty] = useState(false);

  const busy = createShow.isPending || updateShow.isPending;
  const capacityNum = capacity.trim() === "" ? null : Number(capacity);
  const isFestival = kind === "festival";

  // A value that came from somewhere other than this picker (the contract parser writes free text) must not
  // read as blank — it would look cleared while a save still preserves it. Carry it as its own option.
  const ageOptions: PickerOption[] =
    ageRestriction !== "" && !AGE_RESTRICTIONS.some((o) => o.value === ageRestriction)
      ? [...AGE_RESTRICTIONS, { value: ageRestriction, label: ageRestriction }]
      : AGE_RESTRICTIONS;

  const linkOptions = buildRunTourLinkOptions(runs.data?.items, tours.data?.items, showId, date, link);
  const [startingTour, setStartingTour] = useState(false);

  /** "+ New run" mints a run and links this show to it; "+ Start a tour" asks for a name first; every other value
   *  is a direct link. */
  function onLinkChange(v: string) {
    if (v === "new-tour") { setStartingTour(true); return; }
    setBasicsDirty(true);
    if (v === "new-run") {
      void createRun.mutateAsync().then((run) => setLink(`run:${run.runId}`));
      return;
    }
    setLink(v);
  }

  /** Basics' editable fields, shared by the create, this section's own PUT and the footer's fan-out. */
  function basicsFields() {
    return {
      kind,
      venueName: venueName.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      country: country.trim() || null,
      capacity: capacityNum,
      ageRestriction: ageRestriction.trim() || null,
      promoter: promoter.trim() || null,
      // ART-240 — the single link splits back into the mutually-exclusive tourId / runId the server takes.
      tourId: link.startsWith("tour:") ? link.slice("tour:".length) : null,
      runId: link.startsWith("run:") ? link.slice("run:".length) : null,
      ticketUrl: ticketUrl.trim() || null,
      // A festival name is plain text — only a VENUE comes from Places, so only a venue carries a place id.
      venuePlaceId: isFestival ? null : (venuePlaceId || null),
      lat: isFestival ? null : (venueCoords?.lat ?? null),
      lng: isFestival ? null : (venueCoords?.lng ?? null),
    };
  }

  /** The whole-record PUT body for an edit — the show PUT is a full replace, so the fields no section on
   *  this form owns have to ride along or the save nulls them. */
  function editBody(id: string) {
    return {
      showId: id,
      version,
      body: {
        holdTier: detail?.holdTier ?? null,
        holdExpiresOn: detail?.holdExpiresOn ?? null,
        date,
        timezone: detail?.timezone ?? null,
        doorsTime: detail?.doorsTime ?? null,
        // The universal run-of-show block lives on the SHOW row, and this PUT is a full replace — carry it
        // through or a Basics save silently blanks what section 05 wrote.
        showEndTime: detail?.showEndTime ?? null,
        changeoverSupportMin: detail?.changeoverSupportMin ?? null,
        changeoverHeadlinerMin: detail?.changeoverHeadlinerMin ?? null,
        loadInProduction: detail?.loadInProduction ?? null,
        loadOutNotes: detail?.loadOutNotes ?? null,
        venueId: detail?.venueId ?? null,
        notes: detail?.notes ?? null,
        flyerMediaId: detail?.flyerMediaId ?? null,
        contractMediaId: detail?.contractMediaId ?? null,
        ...basicsFields(),
      },
    };
  }

  /**
   * The fan-out's FIRST step: make sure a show exists, and return its id.
   *
   * On an edit this is the id we already have (Basics still commits its own fields through its own PUT, via
   * the registration below). On a create it POSTs, which is what lets every other section be edited before
   * the show exists — they hold local drafts and flush against the id this returns.
   */
  useEnsureShow(async () => {
    if (showId) return showId;
    const created = await createShow.mutateAsync({ date, status: "hold", ...basicsFields() });
    return created.showId!;
  });

  useSectionSave("basics", {
    label: "Basics",
    order: 1,
    // On a create, `useEnsureShow` already wrote these fields — re-PUTting them would be a wasted round
    // trip and a needless version bump, so Basics is only "dirty" for an EDIT the user has touched.
    isDirty: () => !!showId && basicsDirty,
    save: async (id) => { await updateShow.mutateAsync(editBody(id)); setBasicsDirty(false); },
  });

  function onSubmit(e?: FormEvent) {
    e?.preventDefault();
    setSaved(false);
    const basics = basicsFields();

    if (!showId) {
      createShow.mutate(
        { date, status: "hold", ...basics },
        { onSuccess: (created) => onCreated(created.showId!) },
      );
      return;
    }
    // Edit: carry the non-Basics fields from the loaded record so a section-scoped save never nulls them
    // (the PUT is a full replace; the other 8 sections aren't edited here).
    updateShow.mutate(
      {
        showId,
        version,
        body: {
          holdTier: detail?.holdTier ?? null,
          holdExpiresOn: detail?.holdExpiresOn ?? null,
          date,
          timezone: detail?.timezone ?? null,
          doorsTime: detail?.doorsTime ?? null,
          showEndTime: detail?.showEndTime ?? null,
          changeoverSupportMin: detail?.changeoverSupportMin ?? null,
          changeoverHeadlinerMin: detail?.changeoverHeadlinerMin ?? null,
          loadInProduction: detail?.loadInProduction ?? null,
          loadOutNotes: detail?.loadOutNotes ?? null,
          venueId: detail?.venueId ?? null,
          ...basics,
          notes: detail?.notes ?? null,
        },
      },
      { onSuccess: () => setSaved(true) },
    );
  }

  // The type toggle and the venue/festival field, shared by the desktop grid and the phone layout.
  //
  // Show / Festival — the reference's two-pill toggle. It writes `kind` (the API's `show | festival`) and swaps
  // the venue field for a plain Festival field: venues come from Google Places, and there is no festival source,
  // so a festival name is typed rather than resolved.
  //
  // A DIV, not a `<fieldset>`: a `<legend>` renders in the fieldset's own legend box, OUTSIDE the flex formatting
  // context, so the container's `row-gap` separates it from nothing (measured: the toggle sat one `space-2` above
  // the Date field beside it, ART-87). The inner `role="group"` + `aria-label` has always named the group.
  const showTypeField = (
    <div className={styles.pillField}>
      <span className={styles.pillLegend} aria-hidden="true">Show type</span>
      <SegmentedControl
        label="Show type"
        value={kind}
        onChange={(next) => {
          setKind(next);
          setBasicsDirty(true);
          // A festival is typed, never a Places pick, so switching to it unbinds any picked venue.
          if (next === "festival") unbindVenue();
        }}
        options={KINDS}
      />
    </div>
  );
  // `key` forces a REMOUNT on the toggle, so the venue typeahead's open list never survives into the
  // festival's plain field.
  const venueField = isFestival ? (
    <Field
      key="festival"
      label="Festival"
      placeholder="Festival name"
      value={venueName}
      onChange={(e) => { setVenueName(e.target.value); setBasicsDirty(true); }}
    />
  ) : (
    <TypeaheadInput<VenueSuggestion>
      key="venue"
      label="Venue"
      placeholder="Venue name"
      value={venueName}
      onChange={(t) => {
        setVenueName(t);
        setBasicsDirty(true);
        if (venuePlaceId) unbindVenue();
      }}
      onPick={(v) => {
        setVenueName(v.name ?? "");
        setVenuePlaceId(v.placeId ?? "");
        setVenueCoords(v.lat != null && v.lng != null ? { lat: v.lat, lng: v.lng } : null);
        // A picked venue DEFINES the location: all three are replaced together, so a previous venue's
        // state or country cannot survive next to the new city.
        setCity(v.city ?? "");
        setState(v.state ?? "");
        setCountry(v.country ?? "");
        if (v.capacity != null) setCapacity(String(v.capacity));
        setBasicsDirty(true);
      }}
      useSuggestions={useVenueSuggestions}
      itemText={(v) => v.name ?? ""}
      itemKey={(v, i) => v.placeId ?? `${v.name}-${i}`}
      itemPrimary={(v) => v.name}
      itemSecondary={(v) => venueMeta(v) || null}
      footer={<GoogleMapsAttribution />}
    />
  );

  if (mobile) {
    return (
      <form id={BASICS_FORM_ID} className={styles.section} onSubmit={onSubmit}>
        <Field label="Date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setBasicsDirty(true); }} required />
        {showTypeField}
        {venueField}
        <div className={styles.mobilePairNarrow}>
          <Field label="City" value={city} onChange={(e) => { setCity(e.target.value); setBasicsDirty(true); }} />
          <Field label="State" value={state} onChange={(e) => { setState(e.target.value); setBasicsDirty(true); }} />
        </div>
        <div className={styles.mobilePair}>
          <Field label="Country" value={country} onChange={(e) => { setCountry(e.target.value); setBasicsDirty(true); }} />
          <Field label="Capacity" type="number" inputMode="numeric" value={capacity} onChange={(e) => { setCapacity(e.target.value); setBasicsDirty(true); }} />
        </div>
        <Picker
          label="Age restriction"
          value={ageRestriction}
          onChange={(v) => { setAgeRestriction(v); setBasicsDirty(true); }}
          options={ageOptions}
        />
      </form>
    );
  }

  return (
    <form id={BASICS_FORM_ID} className={styles.section} onSubmit={onSubmit}>
      <SectionTitle n={1}>Basics</SectionTitle>
      <div className={styles.grid}>
        <Field label="Date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setBasicsDirty(true); }} required />
        {showTypeField}
        {venueField}
        <Field label="City" value={city} onChange={(e) => { setCity(e.target.value); setBasicsDirty(true); }} />
        <Field label="State" value={state} onChange={(e) => { setState(e.target.value); setBasicsDirty(true); }} />
        <Field label="Country" value={country} onChange={(e) => { setCountry(e.target.value); setBasicsDirty(true); }} />
        <Field label="Capacity" type="number" inputMode="numeric" value={capacity} onChange={(e) => { setCapacity(e.target.value); setBasicsDirty(true); }} />
        {/* Free text on the wire (`age_restriction` has no CHECK), so the reference's option set IS the value
            — the guest's read-only basics view renders it verbatim. "—" clears it. */}
        <Picker
          label="Age restriction"
          value={ageRestriction}
          onChange={(v) => { setAgeRestriction(v); setBasicsDirty(true); }}
          options={ageOptions}
        />
        <Field label="Promoter" value={promoter} onChange={(e) => { setPromoter(e.target.value); setBasicsDirty(true); }} />
        <Field label="Ticket link" type="url" placeholder="Ticket URL" value={ticketUrl} onChange={(e) => { setTicketUrl(e.target.value); setBasicsDirty(true); }} />
        {/* The run/tour link is FULL WIDTH and taller — its trigger carries a two-line row (a run's name +
            the shows it shares), which a half-width, single-height field crops. */}
        <div className={styles.gridFull}>
          <Picker
            label="Link to run or tour"
            value={link}
            onChange={onLinkChange}
            options={linkOptions}
            triggerClassName={styles.linkTrigger}
            hint="Linked shows share travel days, per diem and one member payout."
          />
          {startingTour && (
            <StartTourDialog
              onCancel={() => setStartingTour(false)}
              onCreated={(tourId) => { setStartingTour(false); setBasicsDirty(true); setLink(`tour:${tourId}`); }}
            />
          )}
        </div>
      </div>
      {/* The flyer feeds the row's artwork. It writes through the show PUT, so like the tiers it needs the
          show to exist first. */}
      {showId && detail && (
        <MediaTile
          label="Flyer"
          hint="The show artwork — it appears on the Booking row"
          accept="image/*"
          purpose="show-flyer"
          mediaId={detail.flyerMediaId ?? null}
          onChange={(mediaId) =>
            updateShow.mutate({ showId, version, body: { ...showBodyFrom(detail), flyerMediaId: mediaId } })
          }
        />
      )}
      {/* Tiers are their own sub-resource keyed by showId, so they appear once the show exists — a brand-new
          show has nothing to hang them off yet. */}
      {showId && <TicketTiersBlock showId={showId} />}
      {/* Cancel moved to the pinned footer ribbon (2026-08-23) — it dismisses the whole editor, not this
          section, so sitting inside Basics made it read as "cancel these fields" and put it below the fold
          the moment you scrolled anywhere else. */}
      <div className={styles.actions}>
        {saved && (
          <span className={styles.savedNote} role="status">
            Saved
          </span>
        )}
      </div>
      {/* The primary commit rides the modal's PINNED footer ribbon (Delete · Confirm hold · Save), so it is
          visible at rest instead of below the fold of this scrolling panel. Standalone — no footer host — it
          renders here instead. `form=` is what makes that work either way: it sets the button's form OWNER,
          which is DOM-position-independent, so the portaled button still submits this form AND stays the
          form's default button (Enter from any field keeps submitting). */}
      {/* Standalone only. In the editor the ribbon owns both of these — one Save for every dirty section,
          and a Cancel that dismisses the whole window. Mounted without that chrome (the wizard's own unit
          tests), Basics keeps them so the surface is never left with no way out and no way to commit. */}
      <SectionSave>
        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={onExit}>
            Cancel
          </Button>
          <Button type="submit" form={BASICS_FORM_ID} loading={busy}>
            {showId ? "Save Basics" : "Create show"}
          </Button>
        </div>
      </SectionSave>
    </form>
  );
}

/**
 * 01 Basics' **Ticket tiers** — the contract's ticket bands (General Admission, Balcony…), each with a cap
 * and a face price.
 *
 * <b>Σ caps IS the house capacity</b> (PO, 2026-07-21), enforced server-side on save: entering tiers is
 * entering the house, so the Capacity field above repaints from the same number rather than being typed
 * twice and then disagreeing. Its own sub-resource, so it only mounts once the show exists.
 */
type TierRow = { name: string; cap: string; price: string };

/** Move one record within a list. Tiers and custom expense lines carry no ids on the wire: the server derives
 *  `position` from array order on its full-replace save, so a reorder is purely this splice. */
function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || to < 0 || to >= list.length) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function TicketTiersBlock({ showId }: { showId: string }) {
  const query = useTicketTiers(showId);
  const replace = useReplaceTicketTiers();
  const [rows, setRows] = useState<TierRow[] | null>(null);
  const [saved, setSaved] = useState(false);

  const current: TierRow[] = rows ?? (query.data?.items ?? []).map((t) => ({
    name: t.name ?? "",
    cap: t.cap == null ? "" : String(t.cap),
    price: centsToStr(t.priceCents),
  }));

  function edit(next: TierRow[]) { setRows(next); setSaved(false); }
  const capSum = current.reduce((a, r) => a + (Number(r.cap) || 0), 0);

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    replace.mutate(
      {
        showId,
        items: current
          .filter((r) => r.name.trim() !== "")
          .map((r) => ({
            name: r.name.trim(),
            cap: r.cap.trim() === "" ? undefined : Number(r.cap),
            priceCents: strToCents(r.price),
          })),
      },
      { onSuccess: () => { setRows(null); setSaved(true); } },
    );
  }

  // 01's tiers ride the footer Save too — the block sits inside Basics, so it must commit with it.
  useSectionSave("ticket-tiers", {
    label: "Ticket tiers",
    order: 1.5,
    isDirty: () => rows !== null,
    save: async (id) => {
      await replace.mutateAsync({
        showId: id,
        items: current.filter((r) => r.name.trim() !== "").map((r) => ({
          name: r.name.trim(),
          cap: r.cap.trim() === "" ? undefined : Number(r.cap),
          priceCents: strToCents(r.price),
        })),
      });
      setRows(null);
    },
  });

  if (query.isLoading) return null;

  return (
    <div className={styles.tiers}>
      <span className={styles.tiersLabel}>Ticket tiers</span>
      <RecordList
        dragKind="ticket-tier"
        items={current}
        addLabel="Add tier"
        nameOf={(_, i) => `tier ${i + 1}`}
        onAdd={() => edit([...current, { name: "", cap: "", price: "" }])}
        onMove={(from, to) => edit(moveItem(current, from, to))}
        onRemove={(i) => edit(current.filter((_, idx) => idx !== i))}
        renderFields={(row, i) => (
          <div className={styles.slotRow}>
            <Field
              label={`Tier ${i + 1} name`}
              hideLabel
              placeholder="Tier (e.g. General Admission)"
              value={row.name}
              onChange={(e) => edit(current.map((r, idx) => (idx === i ? { ...r, name: e.target.value } : r)))}
            />
            <Field
              label={`Tier ${i + 1} cap`}
              hideLabel
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="Cap"
              className={styles.tierNum}
              value={row.cap}
              onChange={(e) => edit(current.map((r, idx) => (idx === i ? { ...r, cap: e.target.value } : r)))}
            />
            <Field
              label={`Tier ${i + 1} price`}
              hideLabel
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="Price"
              className={styles.tierNum}
              value={row.price}
              onChange={(e) => edit(current.map((r, idx) => (idx === i ? { ...r, price: e.target.value } : r)))}
            />
          </div>
        )}
      />
      <div className={styles.actions}>
        {/* Tiers ride the footer Save too (registered above), so this is standalone-only like the rest. */}
        <SectionSave>
          <Button variant="ghost" size="sm" onClick={onSave} loading={replace.isPending}>Save tiers</Button>
        </SectionSave>
        {capSum > 0 && (
          <span className={styles.savedNote} role="status">
            {capSum} seats — saving sets the show’s capacity
          </span>
        )}
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}

/**
 * A media tile — the flyer on 01 Basics and the signed contract on 04 Assets. Both are the same contract:
 * drop/click to upload, then the id rides the show's own PUT.
 *
 * The show record has carried `flyerMediaId` / `contractMediaId` since the beginning and NOTHING could set
 * them: they were readable on `ShowDetail` and absent from `UpdateShowRequest`, so the two tiles the
 * reference shows had nowhere to put what they uploaded. `UpdateShowRequest` accepts both as of 2026-08-23.
 */
function MediaTile({
  label,
  hint,
  accept,
  purpose,
  mediaId,
  onChange,
  prompt,
  tall = false,
}: {
  label: string;
  hint: string;
  /** The drop area's call to action; defaults to the desktop "drag or click" line. */
  prompt?: string;
  /** The phone editor's full-height drop area rather than the compact desktop one. */
  tall?: boolean;
  accept: string;
  purpose: string;
  mediaId: string | null;
  onChange: (mediaId: string | null) => void;
}) {
  const { uploadMedia } = useUploadMedia();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const asset = await uploadMedia(file, { kind: mediaKindForFile(file), purpose });
      onChange(asset.mediaId ?? null);
      setErr(null);
    } catch (e) {
      // The upload never reports success without persistence, so a throw here means nothing was stored.
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.mediaTile}>
      <span className={styles.tiersLabel}>{label}</span>
      {mediaId ? (
        <div className={styles.mediaAttached}>
          <span className={styles.mediaOk}>Attached</span>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)}>Remove</Button>
        </div>
      ) : (
        <Dropzone
          // ART-89 — every drop target in this editor is the compact one (*"half of the height they are"*)
          // on the bare tone (*"transparent until hovered, which should slightly darken it"*).
          density={tall ? undefined : "compact"}
          tone="bare"
          accept={accept}
          disabled={busy}
          label={label}
          prompt={busy ? "Uploading…" : (prompt ?? "Drag a file here, or click to browse")}
          hint={hint}
          rejectMessage={`That file type is not accepted here (${accept}).`}
          onFile={(f) => void onFile(f)}
        />
      )}
      {err && <span className={styles.savedNote} role="alert">{err}</span>}
    </div>
  );
}

// ---- Section 02: Lineup ----------------------------------------------------------------------------------
//
// Rebuilt to the reference's shape 2026-08-23: a CHIP STACK over ONE "Search artists to add…" box. It had
// been a numbered row per slot, each with its own inline typeahead — so adding an act was "add a blank row,
// then find the artist inside it" rather than "find the artist, which adds it", and every row carried a
// redundant search field. Chips are avatar · name · role · remove, as the reference lays them out.

function LineupSection({ showId, mobile = false }: { showId: string | undefined; mobile?: boolean }) {
  // The bill lives in the editor's ONE shared lineup draft (ART-209) — the same slots Run of Show reads, so an
  // act added here appears there at once and its set time saves in the same footer Save. Seeding, the own
  // chip, the resync-from-server, the dirty flag and the commit all live in `LineupDraftProvider`; this
  // section is the picker + chip stack over it, plus its own transient query/saved UI.
  const { slots, ownerBandId, addAct, updateSlot, removeSlot, commit, saving } = useLineupDraft();
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState(false);
  const actSource = useActPhotoSource();

  // Is the vendor answering at all? `available: false` means this host holds no Spotify app credentials (or
  // Spotify is down) — nothing a band can fix. The typeahead degrades to a plain text box BY DESIGN — correct,
  // but invisible, so the note below says so.
  const probe = useArtistAutocomplete(query);
  const spotifyAvailable = probe.data?.available ?? true;

  // Hide bands already on the bill from the picker: a band plays ONE slot, so offering one that is already an
  // act would be a pick that does nothing (or, unguarded, a duplicate the save rejects). A thin wrapper around
  // `useArtistSuggestions` so the exclusion rides the same source list TypeaheadInput debounces internally —
  // it closes over the current `slots`, which is why it is a nested hook rather than a module-level one.
  // Free-text and Spotify acts carry no band id, so they are never hidden (two same-named locals are fine).
  function useAvailableActSuggestions(q: string): TypeaheadSuggestions<ActSuggestion> {
    const base = useArtistSuggestions(q);
    const taken = new Set(slots.map((s) => s.friendBandId).filter((id): id is string => !!id));
    return { ...base, items: base.items.filter((a) => !a.friendBandId || !taken.has(a.friendBandId)) };
  }

  /** Search-to-ADD: a pick (or committed free text) appends a chip and clears the box for the next act. */
  function onAdd(name: string, spotifyArtistId: string | null, imageUrl: string | null, friendBandId: string | null = null, avatarMediaId: string | null = null) {
    addAct(name, spotifyArtistId, imageUrl, friendBandId, avatarMediaId);
    setQuery("");
    setSaved(false);
  }
  function update(clientId: string, patch: Partial<LineupSlotDto>) { updateSlot(clientId, patch); setSaved(false); }
  function removeRow(clientId: string) { removeSlot(clientId); setSaved(false); }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    void commit(showId).then(() => setSaved(true)).catch(() => { /* the mutation surfaces its own toast */ });
  }

  return (
    <div className={styles.section}>
      {!mobile && <SectionTitle n={2}>Lineup</SectionTitle>}

      {/* ONE search box that ADDS, as the rapid-add row: pick a suggestion, or type any name and press Enter
          (or hit Add act). Free text has to commit — a local opener has no Spotify page — so the field
          behaves identically whether or not the vendor is answering, and the form is what makes Enter do it.
          The `TypeaheadInput` keeps its VENDOR defaults here (2 chars, 275ms): unlike the Song Bank, every
          keystroke is a round trip. */}
      <form className={styles.quickAdd} onSubmit={(e) => { e.preventDefault(); onAdd(query, null, null); }}>
        <div className={styles.quickAddField}>
          <TypeaheadInput<ActSuggestion>
            label="Add an act"
            placeholder="Search artists to add…"
            value={query}
            onChange={setQuery}
            onPick={(a) => onAdd(a.name, a.spotifyArtistId, a.imageUrl, a.friendBandId, a.avatarMediaId)}
            useSuggestions={useAvailableActSuggestions}
            itemText={(a) => a.name}
            itemKey={(a, i) => a.friendBandId ?? a.spotifyArtistId ?? `${a.name}-${i}`}
            footer={<SpotifyAttribution />}
            itemPrimary={(a) => (
              <span className={styles.actOption}>
                <ActAvatar
                  source={a.friendBandId
                    ? { kind: "band", bandId: a.friendBandId, mediaId: a.avatarMediaId }
                    : { kind: "vendor", url: a.imageUrl }}
                  initials={orgInitials(a.name)}
                  size="sm"
                  shape="circle"
                />
                {a.name}
                {a.isFriend && <Tag tone="accent">Friend</Tag>}
                {/* Says WHY this one is different before it is picked, rather than leaving the mark to
                    appear unexplained on the bill afterwards. */}
                {a.friendBandId && <span className={styles.actOnAc}>On Artist CNTRL</span>}
              </span>
            )}
          />
        </div>
        <AddButton disabled={query.trim() === ""}>Add act</AddButton>
      </form>
      {!spotifyAvailable && (
        <p className={styles.help} role="status">
          Artist search is unavailable right now. You can still type any act name and add it.
        </p>
      )}

      {/* No empty state (PO, ART-89 2026-09-07 — the family the Members one started). The search box and
          its Add act button sit directly above; a titled panel under them announces the same absence at four
          times the size. An empty state earns its room where nothing else on the surface accounts for the
          gap, and here the thing you would do about it is the control you are looking at. */}
      {slots.length > 0 && (
        // ART-89 round two (PO, *"pill buttons … shouldn't take the whole width, same goes for members"*):
        // `wrap` + `density="compact"`, which is the chip line the reference draws (`.lineup-editor` /
        // `.lineup-pill`) and the shape the show PREVIEW has rendered all along. Nothing new — the editor
        // was the one surface passing `framed` WITHOUT `wrap`, so its chips stretched to 930px each.
        <Rows variant="framed" wrap ordered>
          {slots.map((slot, i) => {
            // YOUR OWN act on the bill. It is the slot every run-of-show consumer resolves "our set"
            // through, so it cannot be removed — deleting it used to be a silent way to break attribution,
            // the mobile set clock and the day-of-show reminder. The ROLE stays editable: a band is not
            // necessarily the headliner on its own show, and "headliner" is only the default a first act
            // gets.
            const isUs = isOwnAct(slot, ownerBandId);
            const label = slot.artistName || `Act ${i + 1}`;
            return (
            <Row
              key={slot.clientId}
              variant="framed"
              density="compact"
              // `Avatar`, not a bare <img>: this chip was the only one of the four drawings of this row not
              // using it, so an act with no photo drew an empty grey disc where the SAME act on the show
              // preview drew its initials.
              avatar={<ActAvatar source={actSource(slot)} initials={orgInitials(label)} size="sm" shape="circle" />}
              name={
                <>
                  {/* Name + AC mark are ONE flex item: the mark sits tight to the name and centres against it,
                      instead of a row-gap away and floating on the text baseline. The AC mark — this act
                      resolves to a band on Artist CNTRL (`friendBandId`) — rides the chip for parity with the
                      show preview and the booking row. */}
                  <span className={styles.actNameGroup}>
                    <span className={styles.actNameText}>{slot.artistName}</span>
                    {slot.friendBandId && <AcMark title={`${label} is on Artist CNTRL`} />}
                    {slot.spotifyArtistId && <SpotifyArtistLink spotifyArtistId={slot.spotifyArtistId} name={label} />}
                  </span>
                  {/* The visible "You" mark is gone (PO, 2026-09-08). On a bill you just typed, the chip
                      carrying your own band's name needs no label saying so. The word survives as the
                      chip's accessible name — the ART-92 treatment — because it is the only thing that
                      explains why this one chip has no Remove button. */}
                  {isUs && <span className={styles.srOnly}>Your act</span>}
                </>
              }
              trailing={
                <>
                  {/* The house Picker, not a native <select>: it is the same control the rest of the editor
                      uses, it degrades to the platform picker on a coarse pointer, and it keeps one look
                      across the chip row. `bare` because the chip already names the act — the label rides
                      `aria-label`. */}
                  <Picker
                    bare
                    triggerClassName={styles.rolePicker}
                    label={`${label} role`}
                    value={slot.slotRole ?? "support"}
                    options={SLOT_ROLES.map((r) => ({ value: r.value, label: r.label }))}
                    onChange={(v) => update(slot.clientId, { slotRole: v })}
                  />
                  {!isUs && (
                    <IconButton icon={Trash2} label={`Remove ${label}`} tone="danger" size="field" onClick={() => removeRow(slot.clientId)} />
                  )}
                </>
              }
            />
            );
          })}
        </Rows>
      )}

      <div className={styles.actions}>
        <SectionSave>
          <Button onClick={onSave} loading={saving}>Save lineup</Button>
        </SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}


// ---- Section 03: Members (RSVP + live vote progress) -----------------------------------------------------

type Rsvp = "in" | "out" | "pending";

/** Normalize the server's rsvp string to the three pill states (an unknown/absent value reads as pending). */
function rsvpStatus(rsvp: string | null | undefined): Rsvp {
  return rsvp === "in" ? "in" : rsvp === "out" ? "out" : "pending";
}

/**
 * A member's real name, resolved from the band's crew list.
 *
 * `MemberDto` carries only ids — the show-members endpoint is deliberately a thin join table — so the name
 * comes from the SAME source the picker offers candidates from (`useBandTeam`). Until 2026-08-23 this
 * function did not exist and the chip read `Member 3f2a1b9c`: the first 8 characters of a UUID, shown to a
 * user, in the section whose entire job is choosing which humans get a push notification.
 */
function useMemberNames(): (m: MemberDto) => { name: string; avatarMediaId?: string | null; role?: string | null } {
  const team = useBandTeam();
  const seats = team.data?.items ?? [];
  // The picker's own source (ART-330): it can put an org seat (a manager) on the show, and under the roster lens the
  // band-team read is empty — so a name the crew list lacks comes from here.
  const invitable = useInvitableRows().rows;
  return (m: MemberDto) => {
    const seat = seats.find((x) => x.personId && x.personId === m.personId);
    const other = seat ? undefined : invitable.find((r) => r.personId === m.personId);
    if (other) return { name: other.displayName, avatarMediaId: other.avatarMediaId, role: other.roleKey };
    return {
      // A person on the show who is not (or no longer) a crew seat still renders — as "Invited member"
      // rather than a raw id. Never surface the uuid.
      name: seat?.displayName ?? "Invited member",
      avatarMediaId: seat?.avatarMediaId,
      role: seat?.role,
    };
  };
}

/**
 * The RSVP states as words — the chip no longer prints one, but it still announces it.
 *
 * The state used to ride a text pill in the chip's trailing slot ("Accepted" / "Declined" / "Awaiting
 * reply"). It is now the WHOLE CHIP's wash and glow — what the show PREVIEW has drawn since 2026-08-29 (the
 * wash) and ART-92 (the glow), brought over here on the PO's ruling of 2026-09-08: *"use the color coding we
 * use for the view show modal"*. The two surfaces render the same crew from the same fetch, so a text pill
 * on one and a wash on the other were two renderings of one answer, free to disagree. Colour reaching
 * only sighted users is how an RSVP becomes invisible to everyone else, so the word moves into the chip's
 * accessible name rather than being dropped.
 */
const RSVP_WORD: Record<Rsvp, string> = {
  in: "accepted",
  out: "declined",
  pending: "awaiting reply",
};

/**
 * A member's invite state, read-only.
 *
 * The editor does NOT carry a vote. The legacy desktop editor's section 03 is purely *"pick who gets the
 * push notification for this hold"*; the band VOTES on mobile (`MobileShows`: "When a venue holds a date, it
 * lands here for the band to vote on. Once everyone's in, confirm it."). This rewrite had imported that
 * mobile surface into the editor — a progress bar plus In/Out buttons that let whoever opened the show
 * answer on someone else's behalf, which is not a thing anywhere in the reference. What legacy DOES stamp is
 * `responses[name] = 'pending'` on invite, so the row reads "awaiting reply" straight away; that state is
 * worth showing, and it is all that is shown.
 */
function MembersSection({ showId }: { showId: string | undefined }) {
  const members = useShowMembers(showId);
  const invite = useInviteMembers();
  const setMembers = useSetMembers();
  const nameOf = useMemberNames();
  /** Crew picked but not yet committed — SAVE sends the push, so the picker only stages. */
  const [staged, setStaged] = useState<string[]>([]);
  /** X'd: leaving the show when this saves. STAGED, not immediate, and that is what makes the rule below
   *  possible — an accepted RSVP cannot be restored once the row is gone (nothing may answer for another
   *  person), so a mistaken X must be undoable. */
  const [marked, setMarked] = useState<string[]>([]);
  /** X'd and then picked again from the dropdown — the "removed and re-added" gesture. */
  const [pulled, setPulled] = useState<string[]>([]);

  const rows = members.data?.items ?? [];
  const onRoster = rows.map((m) => m.personId).filter((id): id is string => !!id);
  const rsvpOf = (personId: string) => rows.find((r) => r.personId === personId)?.rsvp;

  /**
   * Removed-and-re-added asks somebody again ONLY if they had DECLINED (PO, 2026-08-29).
   *
   * A pass is spent, so putting them back is a fresh question — the row is dropped and re-made, which is
   * the only way to reach `pending` (an RSVP is that person's own; nothing here may write one). An ACCEPTED
   * or still-unanswered member who is X'd and put back is left exactly as they were: the gesture reads as
   * "never mind" and must not throw away a yes.
   */
  const reAsked = pulled.filter((id) => rsvpOf(id) === "out");
  /** Genuinely new people (the picker cannot offer anyone already on the roster — see `exclude`). */
  const additions = staged.filter((id) => !onRoster.includes(id));
  const toInvite = [...additions, ...reAsked];
  const leaving = marked.filter((id) => !pulled.includes(id));

  // The show's LIVE roster — who is (or will be) on the show after the staged edits: the server members not
  // being removed, plus the staged additions. Financials (section 09) reads this so a pay row appears the
  // instant crew is added here, without a save + reopen.
  const effectiveMembers = [...onRoster.filter((id) => !leaving.includes(id)), ...additions];
  useEditorRoster(effectiveMembers);

  /** Decliners on the roster. The picker OFFERS them (ART-256): picking one is the re-ask in one step, where
   *  the X-then-re-add gesture was the only door and nobody found it. Same commit either way (`reAsked`). */
  const declined = onRoster.filter((id) => rsvpOf(id) === "out");

  /** Picking a MARKED person, or a DECLINED one, puts them back rather than adding a second row; that is the
   *  re-add. Only a decliner is actually re-asked (`reAsked`); a marked yes is simply un-marked. */
  function changeStaged(ids: string[]) {
    const back = ids.filter((id) => marked.includes(id) || declined.includes(id));
    if (back.length > 0) {
      setMarked((m) => m.filter((id) => !back.includes(id)));
      setPulled((p) => [...new Set([...p, ...back])]);
    }
    setStaged(ids.filter((id) => !onRoster.includes(id)));
  }

  /**
   * The one commit, shared by the footer's Save and (standalone only) the section's own button.
   *
   * Two calls at most, in this order: the roster REPLACE drops whoever is leaving plus the decliners being
   * re-asked (their row has to go before a fresh one can exist), then the invite mints rows for everyone
   * being asked and sends the single push. Anybody untouched is on the keep list, so a replace preserves
   * their RSVP and nobody is re-asked by the act of saving.
   */
  async function commit(id: string) {
    const keep = onRoster.filter((p) => !leaving.includes(p) && !reAsked.includes(p));
    if (keep.length !== onRoster.length)
      await setMembers.mutateAsync({ showId: id, members: keep.map((personId) => ({ personId })) });
    if (toInvite.length > 0)
      await invite.mutateAsync({ showId: id, members: toInvite.map((personId) => ({ personId })) });
    setStaged([]);
    setMarked([]);
    setPulled([]);
  }

  // SAVE is what invites (PO, 2026-08-29) — there is no separate Invite button in the editor any more, and
  // the footer names the count so a push never leaves under a word as quiet as "Save".
  //
  // A save that staged nothing is not dirty at all: the section is skipped entirely, no request and no
  // push, so changing only the date never re-asks the roster.
  //
  // It also covers a show that does not exist yet: the fan-out creates the record first, then runs this
  // against it. Without that, picking people before creating the hold would silently drop them.
  usePendingInvites(toInvite.length);
  useSectionSave("members", {
    label: "Members",
    order: 3,
    isDirty: () => leaving.length > 0 || toInvite.length > 0,
    save: commit,
  });

  return (
    <div className={styles.section}>
      <SectionTitle n={3}>Members</SectionTitle>
      {members.isLoading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : members.isError ? (
        <EmptyState
          title="Couldn't load members"
          description={members.error instanceof ApiError ? members.error.message : "Please try again."}
          action={<Button onClick={() => void members.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          {/* The crew picker, not an id box. `CrewInviteePicker` is the same control the opportunity and
              task modals use — it reads the band's seats and offers avatar + name + role.
              `exclude` is the roster MINUS whoever is on their way out, so the list is only people you
              could still add: somebody already on the show is not offered at all (it used to offer them and
              drop the pick in `onChange`, which read as a click that did nothing), and somebody X'd IS
              offered, because picking them again is how you put them back. */}
          {/* The hint sits UNDER the row rather than inside the picker. A field's hint renders below its
              box, so a bottom-aligned commit button next to it lines up with the HINT instead of the box it
              commits — invisible while the button was 16px too tall, obvious once it was the right size.
              It also describes the whole action, not just the picker. */}
          <div className={styles.inviteBlock}>
            <CrewInviteePicker
              value={staged}
              onChange={changeStaged}
              exclude={onRoster.filter((id) => !marked.includes(id) && !(declined.includes(id) && !pulled.includes(id)))}
              label="Invite crew"
              searchPlaceholder="Search your crew…"
              // ART-330 — the invitable set (`core.invitable_people`: band crew ∪ the org seats reaching the band),
              // not the band-team read: under the roster lens a manager holds no band seat, and band seats are
              // readable only by people on the band, so that read came back empty and the picker said "No matches".
              source="invitable"
              // Self is a candidate HERE (ART-256): putting yourself on a show (or back on it after you
              // passed) is a real act, and the picker's default hid you, so it could not be done at all.
              includeSelf
              excludeOwner
            />
            {/* Only where it is the ONLY way to commit. In the editor the footer's "Save and invite N" does
                this and says so, and two controls for one act is how you get a roster invited twice.
                `field`, so the button is the height of the picker it commits — the default `md` stood 16px
                taller than the control beside it. */}
            <SectionSave>
              <Button
                size="field"
                onClick={() => showId && void commit(showId)}
                disabled={!showId || (leaving.length === 0 && toInvite.length === 0)}
                loading={invite.isPending || setMembers.isPending}
              >
                Save {toInvite.length > 0 ? `and invite ${toInvite.length}` : ""}
              </Button>
            </SectionSave>
          </div>
          {/* The staging line, and ONLY when something is staged (PO, 2026-09-08). *"Nobody staged yet."*
              was a permanent paragraph reporting an absence the picker directly above it already accounts
              for — the same argument that took the empty-state panel out of this section, one size down.
              It is the line's whole job to say what Save is about to do; with nothing staged there is
              nothing for it to say. */}
          {(toInvite.length > 0 || leaving.length > 0) && (
            <p className={styles.help}>
              {[
                toInvite.length > 0 ? `${toInvite.length} will get a push when you save` : "",
                leaving.length > 0 ? `${leaving.length} leaving this show` : "",
              ]
                .filter(Boolean)
                .join(" · ") + "."}
            </p>
          )}

          {/* No empty state. A titled panel here said the same thing as the picker above it, at four times
              the size, and was the tallest thing in a section whose whole job is one search box (PO, ART-89
              2026-09-07). An empty state earns its room where nothing else on the surface accounts for the
              absence; here the control you would use does. */}
          {rows.length > 0 && (
            <Rows variant="framed" wrap>
              {rows.map((m) => {
                const status = rsvpStatus(m.rsvp);
                const who = nameOf(m);
                const isLeaving = !!m.personId && leaving.includes(m.personId);
                return (
                  <Row
                    key={m.showMemberId}
                    variant="framed"
                    density="compact"
                    // The answer is the chip, not a pill inside it: a wash plus a glow in the status hue,
                    // the same three tokens the show preview paints. A chip on its way OUT carries no
                    // status colour — its RSVP is about to be discarded, and the dim plus the tag beside
                    // it are what that row is saying.
                    className={isLeaving ? `${styles.memberChip} ${styles.memberLeaving}` : styles.memberChip}
                    data-rsvp={isLeaving ? undefined : status}
                    avatar={
                      <PersonAvatar
                        personId={m.personId}
                        avatarMediaId={who.avatarMediaId}
                        initials={orgInitials(who.name)}
                        size="sm"
                        shape="circle"
                      />
                    }
                    name={
                      <>
                        <span className={styles.memberNameText}>{who.name}</span>
                        {/* ART-171: RoleLabel gives the AV mark for a full-access member; words unchanged. */}
                        {who.role && <RoleLabel role={who.role} className={styles.memberRole} />}
                        {!isLeaving && <span className={styles.srOnly}>· {RSVP_WORD[status]}</span>}
                      </>
                    }
                    trailing={
                      isLeaving ? (
                        // Marked, not gone: the row stays until Save so the X is undoable — pick them again
                        // in the dropdown above and they are simply back.
                        <span className={styles.memberLeavingTag}>Leaving on save</span>
                      ) : (
                        // Take somebody OFF the show. STAGED — it lands with Save, and re-adding them from
                        // the dropdown is what asks a person who DECLINED to look again. Somebody who
                        // accepted (or has not answered) is left exactly as they were: an RSVP is theirs,
                        // and this must not throw a yes away.
                        <IconButton
                          icon={X}
                          label={`Remove ${who.name} from this show`}
                          title="Remove"
                          tone="danger"
                          size="xs"
                          disabled={!m.personId}
                          onClick={() => {
                            if (!m.personId) return;
                            const id = m.personId;
                            setMarked((v) => [...v, id]);
                            // An X after a re-pick cancels the re-ask too, or `leaving` (marked minus pulled)
                            // would silently keep them.
                            setPulled((p) => p.filter((x) => x !== id));
                          }}
                        />
                      )
                    }
                  />
                );
              })}
            </Rows>
          )}
        </>
      )}
    </div>
  );
}

// ---- Section 04: Assets (signed contract upload) ---------------------------------------------------------

function AssetsSection({ showId, detail, version, mobile = false }: {
  showId: string | undefined; detail: ShowDetail | undefined; version: number; mobile?: boolean;
}) {
  const updateShow = useUpdateShow();

  return (
    <div className={styles.section}>
      {!mobile && <SectionTitle n={4}>Assets</SectionTitle>}
      {/* The contract tile the reference has had all along. `contractMediaId` was readable on the show and
          settable by nothing until 2026-08-23. */}
      <MediaTile
        label="Contract"
        hint={mobile ? "PDF from Files or Photos" : "PDF, or a photo of the signed page"}
        prompt={mobile ? "Attach contract" : undefined}
        tall={mobile}
        accept=".pdf,application/pdf,image/*"
        purpose="show-contract"
        mediaId={detail?.contractMediaId ?? null}
        onChange={(mediaId) => {
          // Attaching writes through the SHOW record, so it needs one. The upload itself is band-scoped and
          // already succeeded; before the show exists there is nothing to attach it to.
          if (!showId || !detail) return;
          updateShow.mutate({ showId, version, body: { ...showBodyFrom(detail), contractMediaId: mediaId } });
        }}
      />
    </div>
  );
}

// ---- Section 05: Run of Show (load-in/out, soundcheck, stage; per-band collaborative rows) ----------------
//
// The reference is a STRUCTURED form, not a free list: named rows (doors · changeover · show end · load-in),
// each duration picked from a FIXED option set, and a soundcheck/set grid beneath. Rebuilt to that shape
// 2026-08-23 — it had been one generic repeating row (a section dropdown + free-text label + a time), so
// every constraint the reference encodes was left to the user to remember and type.
//
// The storage did not have to change: `show_schedule_items` is already per-authoring-band with a
// `section`/`label`/`starts_at`/`duration_min` shape, and `duration_min` was simply never surfaced. The
// section vocabulary gained `doors` and `show_end` so the named rows have somewhere to live.


/**
 * Durations, transcribed from the reference's own sets. A parsed value that is not on a list snaps to the
 * nearest, so a screenshot's "22 min" always lands on a real option instead of rendering blank.
 *
 * These drifted when the section was first rebuilt — support sets gained a 35 and a 55 the reference does not
 * offer, headliner sets lost the 50 and invented a 135. Corrected 2026-08-26 and pinned by a test, because a
 * set-length list is exactly the kind of thing that looks right at a glance and is quietly wrong.
 */
const CHANGEOVER_MIN = [10, 15, 20, 25, 30];
const SOUNDCHECK_MIN = [30, 45, 60, 90, 120];
const SUPPORT_SET_MIN = [15, 20, 25, 30, 40, 45, 50, 60];
const HEADLINER_SET_MIN = [45, 50, 60, 75, 90, 105, 120, 150];

/** The reference's defaults for the show-wide controls — a run of show opens usable, not blank. */
const DEFAULT_CHANGEOVER_MIN = 20;
// 11pm, written in the 12-hour form every other time in this section renders (ART-209). It was "23:00", the
// only 24-hour clock anywhere on the show — the storage is still `HH:mm` (see `buildUniversal`), this is the
// value the Show-end FIELD opens showing.
const DEFAULT_SHOW_END = "11pm";

/**
 * `45 min` · `1 hr` · `1 hr 15 min` — the reference's own phrasing. Rendering 75 as "75 min" is not wrong so
 * much as unreadable at a glance: nobody thinks of a headline set in minutes past an hour.
 */
function durationLabel(m: number): string {
  if (m < 60) return `${m} min`;
  if (m % 60 === 0) return `${m / 60} hr`;
  return `${Math.floor(m / 60)} hr ${m % 60} min`;
}

function minuteOptions(set: readonly number[]): PickerOption[] {
  return [{ value: "", label: "—" }, ...set.map((m) => ({ value: String(m), label: durationLabel(m) }))];
}

/** Lineup role → chronological rank, so the grid reads opener at the top and the headliner at the bottom. */
const ROLE_RANK: Record<string, number> = {
  local_support: 0, opener: 1, support: 2, direct_support: 3, headliner: 4, co_headliner: 4,
};
const isHeadlinerRole = (role: string | null | undefined) => role === "headliner" || role === "co_headliner";

/** Snap an arbitrary duration onto the nearest allowed option — a screenshot-parsed 22 becomes 20, so a
 *  parsed value always slots into an existing dropdown instead of rendering as a blank select. */
function snapMinutes(value: number | null | undefined, set: readonly number[]): string {
  if (value == null) return "";
  return String(set.reduce((best, m) => (Math.abs(m - value) < Math.abs(best - value) ? m : best), set[0]));
}

/**
 * The named rows the reference lays out, in its order — UNCHANGED on screen, re-homed underneath (Phase 1).
 *
 * Each row now declares WHERE its value lives, because one flat list was hiding three different grains:
 *
 *  • `universal` — a show-wide fact with exactly one value (doors, show end, the two changeovers). It is a
 *    COLUMN on `artist.shows`, not a schedule row: a row shape can represent two doors times and a column
 *    cannot, doors renders on list surfaces where a child row costs a join, and every schedule row must name
 *    an authoring band — which a venue fact has none of.
 *  • `ours` — a schedule row about the AUTHORING band's own act, attributed with that act's `slotId`. This is
 *    what makes "our set" resolvable: `section === 'stage'` alone matched four different rows.
 *  • neither — a schedule row we cannot yet attribute (the support act's set). It stays unattributed rather
 *    than being guessed at; the per-act grid gives it a real act.
 *
 * `section` is only meaningful for the schedule-row kinds. `doors`/`show_end` have left the vocabulary as
 * far as this form is concerned — the API still accepts them for one more deploy (expand/contract).
 */
/**
 * The four SHOW-WIDE controls. One value each per show, stored as columns on the show record (D7), not as
 * schedule rows — a row shape can represent two doors times and a column cannot.
 *
 * The reference's fourth control here is "Headliner load-in". It is gone by ruling (D5): the headliner's
 * load-in is that act's own `Load in` cell in the grid below, and keeping a second field for it would be the
 * same fact in two places. Doors takes the slot — legacy had it stranded at the bottom of the section, an
 * artefact of merging its old "Load in / out" and "Stage schedule" sections, and the four universal timings
 * belong together.
 */
const UNIVERSAL_CONTROLS: {
  key: "changeoverSupportMin" | "changeoverHeadlinerMin" | "showEndTime" | "doorsTime";
  label: string;
  minutes?: readonly number[];
}[] = [
  { key: "changeoverSupportMin", label: "Changeover · support", minutes: CHANGEOVER_MIN },
  { key: "changeoverHeadlinerMin", label: "Changeover · headliner", minutes: CHANGEOVER_MIN },
  { key: "showEndTime", label: "Show end" },
  { key: "doorsTime", label: "Doors" },
];

/** One act's row in the grid — the four schedule rows it owns, held as the strings the inputs show. */
type ActTimes = {
  loadIn: string;
  soundcheck: string;
  scMin: string;
  setTime: string;
  setLength: string;
  loadOut: string;
  /** Derived by the cascade and still overwritable. Editor-only — never persisted, never sent. */
  autoStart: boolean;
};

const EMPTY_ACT: ActTimes = {
  loadIn: "", soundcheck: "", scMin: "", setTime: "", setLength: "", loadOut: "", autoStart: true,
};

/**
 * The grid's columns, in render order — **ONE list drives the header AND the cells**.
 *
 * It briefly did not, and the result was the worst kind of UI bug: this list described the four SECTIONS
 * (what gets persisted) while each row rendered SIX controls (the two duration pickers as well). The header
 * therefore ran out two columns early, so "Set time" sat above the soundcheck-minutes picker, "Load out" sat
 * above the SET TIME field, and the last two controls had no header at all. Every accessible name was
 * correct, so the tests passed and a screen reader was fine — it was purely the visible headers that lied,
 * and typing into the box under "Load out" changed the set time.
 *
 * A `time` column names its `durationKey` partner so persistence can pair them without a second list.
 */
type ActColumn = {
  key: keyof ActTimes;
  label: string;
  kind: "time" | "duration";
  /** `time` only: the schedule section it writes, and its optional duration partner. */
  section?: string;
  placeholder?: string;
  durationKey?: keyof ActTimes;
  /** `duration` only: the fixed option set. Set length varies by role, so it resolves per row. */
  options?: readonly number[];
  /** Spoken name, where the column HEADING is an abbreviation. "SC length" is a fine header, a poor label. */
  a11y?: string;
};

const ACT_COLUMNS: ActColumn[] = [
  { key: "loadIn", label: "Load in", kind: "time", section: "load_in", placeholder: "3:00 pm" },
  { key: "soundcheck", label: "Soundcheck", kind: "time", section: "soundcheck", placeholder: "3:30 pm", durationKey: "scMin" },
  // "SC length", not the reference's "SC MIN" (PO, 2026-08-26) — it pairs with "Set length" beside it, and
  // the two columns are the same kind of quantity. A deliberate copy deviation, not a parity miss.
  { key: "scMin", label: "SC length", kind: "duration", options: SOUNDCHECK_MIN, a11y: "soundcheck length" },
  { key: "setTime", label: "Set time", kind: "time", section: "stage", placeholder: "Set time", durationKey: "setLength" },
  { key: "setLength", label: "Set length", kind: "duration" },
  { key: "loadOut", label: "Load out", kind: "time", section: "load_out", placeholder: "Load out" },
];

/** The columns that persist — a schedule row each, paired with their duration where they have one. */
const ACT_TIME_COLUMNS = ACT_COLUMNS.filter((c) => c.kind === "time");

/**
 * Editor section 05 — the reference's Run of Show: a show-wide control strip over a PER-ACT GRID.
 *
 * The 2026-08-23 rebuild took the reference's option sets and dropped its structure, flattening a grid over
 * the lineup into nine fixed rows. A three-act bill collapsed into a single "Support set", four rows shared
 * `section='stage'` so nothing downstream could tell whose set was whose, and the only way to enter a time
 * was to hunt through a 97-option dropdown. This is that structure restored on the Phase-1 storage.
 *
 * Times are FREE TEXT with a forgiving parser ({@link parseLooseTime}) exactly as the reference has them —
 * `2pm`, `3:15`, `8pm` all land. Only durations are dropdowns, because those really are a fixed set.
 */
function RunOfShowSection({ showId }: { showId: string | undefined }) {
  const query = useRunOfShow(showId);
  const update = useUpdateRunOfShow();
  const parse = useParseRunOfShow();
  // The bill comes from the editor's ONE live lineup draft (ART-209), so an act added in section 02 shows up
  // here at once — no save required. Per-act grid state is keyed by the slot's stable `clientId`, because a
  // not-yet-saved act has no `slotId` to key on; the `slotId` is looked up from `draft.slotsRef` at save time,
  // which — the fan-out saving Lineup (order 2) before this (order 5) — carries the freshly minted ids.
  const draft = useLineupDraft();
  const [acts, setActs] = useState<Record<string, ActTimes> | null>(null);
  const [universal, setUniversal] = useState<Record<string, string> | null>(null);
  const [notes, setNotes] = useState<{ loadInProduction: string; loadOutNotes: string } | null>(null);
  const [parseNote, setParseNote] = useState("");
  const [saved, setSaved] = useState(false);
  // Keyed by the mounted band, never `show.data` — on a show being CREATED there is no show read yet, which
  // left our own act's photo unresolved here while 02 · Lineup drew it (ART-298).
  const actSource = useActPhotoSource();

  const serverRows = query.data?.items;
  const serverUniversal = query.data?.universal;

  /** The bill, chronological — opener at the top, headliner at the bottom, as the reference orders it. Read
   *  from the live draft (not the server lineup), so an unsaved act is on the grid the moment it is added. */
  const bill = useMemo<DraftSlot[]>(() =>
    [...draft.slots].sort((a, b) =>
      (ROLE_RANK[a.slotRole ?? "opener"] ?? 1) - (ROLE_RANK[b.slotRole ?? "opener"] ?? 1)
      || (a.position ?? 0) - (b.position ?? 0)),
  [draft.slots]);

  /** Seed each act's row from the schedule rows attributed to its slot. Keyed by `clientId`; a not-yet-saved
   *  act has a null `slotId`, so it matches no server row and opens blank. */
  const seededActs: Record<string, ActTimes> = acts ?? Object.fromEntries(
    bill.map((slot: DraftSlot) => {
      const mine = (serverRows ?? []).filter((i) => slot.slotId != null && i.slotId === slot.slotId);
      const at = (section: string) => mine.find((i) => i.section === section);
      const setRow = at("stage");
      const scRow = at("soundcheck");
      return [slot.clientId, {
        loadIn: formatShowTime(parseWallClock(at("load_in")?.startsAt)),
        soundcheck: formatShowTime(parseWallClock(scRow?.startsAt)),
        scMin: snapMinutes(scRow?.durationMin, SOUNDCHECK_MIN),
        setTime: formatShowTime(parseWallClock(setRow?.startsAt)),
        setLength: snapMinutes(setRow?.durationMin, isHeadlinerRole(slot.slotRole) ? HEADLINER_SET_MIN : SUPPORT_SET_MIN),
        loadOut: formatShowTime(parseWallClock(at("load_out")?.startsAt)),
        // A time that came back from the server was committed by someone, so it ANCHORS. Seeding these as
        // auto would let the cascade quietly rewrite a saved running order the moment the section opened.
        autoStart: setRow?.startsAt == null,
      }];
    }),
  );

  /** The show-wide block, defaulted the way the reference defaults it so the section opens usable. */
  const seededUniversal: Record<string, string> = universal ?? {
    // Doors and Show-end are FREE-TEXT fields, so they open showing the 12-hour form every other time in the
    // section uses — never the raw `HH:mm` the API stores (ART-209: a saved `23:00:00` was rendering "23:00",
    // the one 24-hour clock in the app). `buildUniversal` parses it back to `HH:mm` on save, so the wire is
    // unchanged. This mirrors the act cells, which have always seeded through `formatShowTime(parseWallClock)`.
    doorsTime: formatShowTime(parseWallClock(serverUniversal?.doorsTime)),
    showEndTime: formatShowTime(parseWallClock(serverUniversal?.showEndTime)) || DEFAULT_SHOW_END,
    // Snapped, not just stringified: a screenshot-parsed 22 is not on the changeover set, and an off-set
    // value renders the picker BLANK — which reads as "nothing recorded" for a value that is recorded.
    changeoverSupportMin: snapMinutes(serverUniversal?.changeoverSupportMin ?? DEFAULT_CHANGEOVER_MIN, CHANGEOVER_MIN),
    changeoverHeadlinerMin: snapMinutes(
      serverUniversal?.changeoverHeadlinerMin ?? serverUniversal?.changeoverSupportMin ?? DEFAULT_CHANGEOVER_MIN,
      CHANGEOVER_MIN),
  };

  const seededNotes = notes ?? {
    loadInProduction: serverUniversal?.loadInProduction ?? "",
    loadOutNotes: serverUniversal?.loadOutNotes ?? "",
  };

  /** Rows the grid does not own — another act's, or an older unattributed one — ride a save untouched. */
  const unclaimed = (serverRows ?? []).filter(
    (i) => !bill.some((s: DraftSlot) => s.slotId != null && s.slotId === i.slotId)
           || !ACT_TIME_COLUMNS.some((c) => c.section === i.section),
  );

  function patchAct(clientId: string, p: Partial<ActTimes>) {
    setActs({ ...seededActs, [clientId]: { ...(seededActs[clientId] ?? EMPTY_ACT), ...p } });
    setSaved(false);
  }

  const gaps = {
    support: Number.parseInt(seededUniversal.changeoverSupportMin, 10) || 0,
    headliner: Number.parseInt(seededUniversal.changeoverHeadlinerMin, 10) || 0,
  };

  /** The bill as the cascade sees it — grid order IS chronological order, so no re-sorting. `slotId` here is
   *  the cascade's opaque row key, so it carries the stable `clientId` (a new act has no real slotId yet). */
  function cascadeRows(): CascadeAct[] {
    return bill.map((slot: DraftSlot) => {
      const r = seededActs[slot.clientId] ?? EMPTY_ACT;
      return {
        slotId: slot.clientId,
        isHeadliner: isHeadlinerRole(slot.slotRole),
        setTime: r.setTime,
        setLength: r.setLength,
        autoStart: r.autoStart,
      };
    });
  }

  /** Fold a cascade result back onto the grid state, leaving the non-set columns untouched. */
  function applyCascaded(next: CascadeAct[]) {
    const merged = { ...seededActs };
    for (const c of next) {
      merged[c.slotId] = { ...(merged[c.slotId] ?? EMPTY_ACT), setLength: c.setLength, setTime: c.setTime, autoStart: c.autoStart };
    }
    setActs(merged);
    setSaved(false);
  }

  function onSetTime(index: number, value: string) {
    applyCascaded(setTimeEdited(cascadeRows(), index, value, gaps.support, gaps.headliner));
  }
  function onSetLength(index: number, value: string) {
    applyCascaded(setLengthEdited(cascadeRows(), index, value, gaps.support, gaps.headliner));
  }
  function patchUniversal(key: string, value: string) {
    setUniversal((prev) => {
      const base = prev ?? seededUniversal;
      const next = { ...base, [key]: value };
      // The headliner changeover MIRRORS the support one until it is touched — the reference's behaviour,
      // and the reason two controls do not mean twice the typing on a normal bill.
      if (key === "changeoverSupportMin" && base.changeoverHeadlinerMin === base.changeoverSupportMin) {
        next.changeoverHeadlinerMin = value;
      }
      return next;
    });
    setSaved(false);
    // A changed gap moves every derived start — the reference re-runs the cascade on exactly this.
    const support = key === "changeoverSupportMin" ? Number.parseInt(value, 10) || 0 : gaps.support;
    const headliner = key === "changeoverHeadlinerMin"
      ? Number.parseInt(value, 10) || 0
      : (key === "changeoverSupportMin"
        && seededUniversal.changeoverHeadlinerMin === seededUniversal.changeoverSupportMin
          ? Number.parseInt(value, 10) || 0
          : gaps.headliner);
    if (key.startsWith("changeover")) applyCascaded(applyCascade(cascadeRows(), support, headliner));
  }

  /** The schedule rows a save writes: up to four per act, only the ones actually filled in. */
  function buildRosItems() {
    // Resolve each bill slot's slotId from the LIVE draft ref, not the render snapshot: in the footer fan-out
    // the lineup commits (order 2) just before this runs (order 5), and its freshly minted slotIds land in the
    // ref synchronously. An act with no slotId yet (lineup save skipped or failed) cannot be attributed, so its
    // rows are dropped rather than written with a null slot.
    const slotIdFor = (clientId: string) =>
      draft.slotsRef.current.find((s) => s.clientId === clientId)?.slotId ?? null;
    const named = bill.flatMap((slot: DraftSlot) => {
      const slotId = slotIdFor(slot.clientId);
      if (slotId == null) return [];
      const row = seededActs[slot.clientId] ?? EMPTY_ACT;
      return ACT_TIME_COLUMNS.flatMap((col) => {
        const startsAt = toWallClock(parseLooseTime(String(row[col.key])));
        const duration = col.durationKey ? String(row[col.durationKey]) : "";
        if (startsAt == null && duration === "") return [];
        return [{
          section: col.section!,
          // Attribution is the row's identity; the act NAME rides `label` so the day sheet and the shared
          // view stay readable without resolving the lineup — the same convention the screenshot parser uses.
          slotId,
          label: slot.artistName || null,
          startsAt,
          durationMin: duration === "" ? null : Number(duration),
          notes: null,
        }];
      });
    });
    const kept = unclaimed.map((i) => ({
      section: i.section ?? "custom",
      slotId: i.slotId ?? null,
      label: i.label ?? null,
      startsAt: i.startsAt ?? null,
      durationMin: i.durationMin ?? null,
      notes: i.notes ?? null,
    }));
    return [...named, ...kept];
  }

  function buildUniversal() {
    const num = (v: string) => (v === "" ? null : Number(v));
    return {
      doorsTime: toWallClock(parseLooseTime(seededUniversal.doorsTime)),
      showEndTime: toWallClock(parseLooseTime(seededUniversal.showEndTime)),
      changeoverSupportMin: num(seededUniversal.changeoverSupportMin),
      changeoverHeadlinerMin: num(seededUniversal.changeoverHeadlinerMin),
      loadInProduction: seededNotes.loadInProduction.trim() || null,
      loadOutNotes: seededNotes.loadOutNotes.trim() || null,
    };
  }

  /**
   * Drop a day sheet → fill the grid from it.
   *
   * Nothing is persisted: the parse proposes, the grid shows the proposal, and the user presses Save. Two
   * rules from the reference make it safe to point at a photograph — a parsed field only fills a cell that is
   * EMPTY (a value you typed is never clobbered by a screenshot), and every duration snaps onto its own
   * option set so a parsed "22 min" lands on a real choice instead of blanking the picker.
   */
  async function onScreenshot(file: File) {
    if (!showId) { setParseNote("Save the show first, then drop the day sheet."); return; }
    setParseNote("Reading screenshot…");
    try {
      const body = await fileToDataUrl(file);
      const res = await parse.mutateAsync({ showId, body });

      const nextActs = { ...seededActs };
      let filled = 0;
      for (const item of res.items ?? []) {
        // The parser attributes to a server slotId; the grid is keyed by clientId, so resolve through the bill.
        const slot = item.slotId ? bill.find((b: DraftSlot) => b.slotId === item.slotId) : undefined;
        if (!slot || !(slot.clientId in nextActs)) continue;
        const row = { ...nextActs[slot.clientId] };
        const time = formatShowTime(parseWallClock(item.startsAt));
        const put = (key: keyof ActTimes, v: string) => {
          if (v !== "" && row[key] === "") { (row as Record<string, unknown>)[key] = v; filled++; }
        };
        if (item.section === "load_in") put("loadIn", time);
        if (item.section === "load_out") put("loadOut", time);
        if (item.section === "soundcheck") {
          put("soundcheck", time);
          put("scMin", snapMinutes(item.durationMin, SOUNDCHECK_MIN));
        }
        if (item.section === "stage") {
          put("setTime", time);
          put("setLength", snapMinutes(
            item.durationMin, isHeadlinerRole(slot.slotRole) ? HEADLINER_SET_MIN : SUPPORT_SET_MIN));
          if (row.setTime !== "") row.autoStart = false;   // a parsed time is a stated one: it anchors
        }
        nextActs[slot.clientId] = row;
      }
      setActs(nextActs);

      const u = res.universal;
      if (u) {
        const next = { ...seededUniversal };
        const putU = (k: string, v: string) => { if (v !== "" && (next[k] ?? "") === "") next[k] = v; };
        putU("doorsTime", formatShowTime(parseWallClock(u.doorsTime)));
        putU("showEndTime", formatShowTime(parseWallClock(u.showEndTime)));
        if (u.changeoverSupportMin != null) next.changeoverSupportMin = snapMinutes(u.changeoverSupportMin, CHANGEOVER_MIN);
        if (u.changeoverHeadlinerMin != null) next.changeoverHeadlinerMin = snapMinutes(u.changeoverHeadlinerMin, CHANGEOVER_MIN);
        setUniversal(next);
      }
      setSaved(false);

      // Say what happened, including what it could NOT place — a silent partial fill reads as a bad parse.
      const parts: string[] = [];
      if (filled > 0) parts.push(`${filled} field${filled === 1 ? "" : "s"} filled`);
      if (res.unmatched?.length) parts.push(`couldn't match: ${res.unmatched.join(", ")}`);
      setParseNote(parts.length ? parts.join(" · ") : "Nothing recognised — enter it below.");
    } catch (e) {
      setParseNote(errorMessage(e, "Couldn't read that screenshot — enter it below."));
    }
  }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    update.mutate(
      { showId, body: { items: buildRosItems(), universal: buildUniversal() } },
      { onSuccess: () => { setActs(null); setUniversal(null); setNotes(null); setSaved(true); } },
    );
  }

  useSectionSave("run-of-show", {
    label: "Run of Show",
    order: 5,
    isDirty: () => acts !== null || universal !== null || notes !== null,
    save: async (id) => {
      await update.mutateAsync({ showId: id, body: { items: buildRosItems(), universal: buildUniversal() } });
      setActs(null);
      setUniversal(null);
      setNotes(null);
    },
  });

  /**
   * The running order as it currently stands. Derived every render from the grid's own state — never stored,
   * never fetched — so it can only ever show what is actually entered.
   */
  const preview = buildDosPreview({
    acts: bill.map((slot: DraftSlot) => {
      const r = seededActs[slot.clientId] ?? EMPTY_ACT;
      return { name: slot.artistName ?? "", ...r };
    }),
    doors: seededUniversal.doorsTime ?? "",
    showEnd: seededUniversal.showEndTime ?? "",
  });

  if (query.isLoading) return <SectionLoading n={5} label="Run of Show" />;
  if (query.isError) return <SectionError n={5} label="Run of Show" query={query} />;

  return (
    <div className={styles.section}>
      <SectionTitle n={5}>Run of Show</SectionTitle>

      <div className={styles.rosUniversal}>
        {UNIVERSAL_CONTROLS.map((c) => (
          <div key={c.key} className={styles.rosUniversalField}>
            {c.minutes ? (
              <Picker
                label={c.label}
                value={seededUniversal[c.key] ?? ""}
                options={minuteOptions(c.minutes)}
                onChange={(v) => patchUniversal(c.key, v)}
              />
            ) : (
              <Field
                label={c.label}
                placeholder="11:00 pm"
                value={seededUniversal[c.key] ?? ""}
                hint={isNextDay(toWallClock(parseLooseTime(seededUniversal[c.key])) ?? undefined) ? "Next day" : undefined}
                onChange={(e) => patchUniversal(c.key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      {/* The screenshot path. The venue's day sheet usually arrives as an image in a text thread, and
          retyping it is the actual job this section replaces. Mirrors the Travel dropzone exactly. */}
      <div className={styles.rosDropzone}>
        <Dropzone
          density="compact"
          tone="bare"
          accept="image/*"
          disabled={parse.isPending || !showId}
          label="Day sheet"
          prompt={parse.isPending ? "Reading…" : "Drop a run-of-show screenshot, or click to browse"}
          hint="Fills the empty cells below. Nothing is saved until you press Save."
          rejectMessage="That doesn't look like an image."
          onFile={(f) => void onScreenshot(f)}
        />
        {parseNote && <span className={styles.savedNote} role="status">{parseNote}</span>}
      </div>

      {bill.length === 0 ? (
        <p className={styles.help}>Add acts in 02 · Lineup to build the run of show.</p>
      ) : (
        <div className={styles.rosGrid} role="table" aria-label="Run of show by act">
          <div className={styles.rosGridHead} role="row">
            <span role="columnheader">Act</span>
            {ACT_COLUMNS.map((c) => (
              <span key={c.key} role="columnheader" data-col={c.key}>{c.label}</span>
            ))}
          </div>
          {bill.map((slot: DraftSlot, index: number) => {
            const row = seededActs[slot.clientId] ?? EMPTY_ACT;
            const head = isHeadlinerRole(slot.slotRole);
            const setLengths = head ? HEADLINER_SET_MIN : SUPPORT_SET_MIN;
            return (
              <div key={slot.clientId} className={styles.rosGridRow} role="row">
                <span className={styles.rosAct}>
                  {/* Same act photo as 02 · Lineup, from the same resolver AND through the same `Avatar` —
                      one act reads the same in both sections, including its initials fallback. This used to
                      say the first half while drawing a blank grey disc where 02 drew a face. */}
                  <ActAvatar
                    source={actSource(slot)}
                    initials={orgInitials(slot.artistName || "Untitled act")}
                    size="sm"
                    shape="circle"
                  />
                  <span className={styles.rosActName}>{slot.artistName || "Untitled act"}</span>
                  {head && <Tag tone="accent" size="sm">HL</Tag>}
                </span>
                {ACT_COLUMNS.map((col) => {
                  const who = slot.artistName || "Act";
                  const name = `${who} ${col.a11y ?? col.label.toLowerCase()}`;
                  return (
                    <span key={col.key} className={styles.rosCell} data-col={col.key}>
                      {/* The column header is hidden at the phone breakpoint, where the grid becomes stacked
                          cards — so each cell carries its own visible label there. */}
                      <span className={styles.rosCellLabel} aria-hidden="true">{col.label}</span>
                      {col.kind === "time" ? (
                        <Field
                          hideLabel
                          label={name}
                          placeholder={col.placeholder}
                          value={String(row[col.key])}
                          onChange={(e) => (col.key === "setTime"
                            ? onSetTime(index, e.target.value)
                            : patchAct(slot.clientId, { [col.key]: e.target.value }))}
                        />
                      ) : (
                        <Picker
                          bare
                          triggerClassName={styles.rosDuration}
                          label={name}
                          value={String(row[col.key])}
                          options={minuteOptions(col.options ?? setLengths)}
                          onChange={(v) => (col.key === "setLength"
                            ? onSetLength(index, v)
                            : patchAct(slot.clientId, { [col.key]: v }))}
                        />
                      )}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {preview.length > 0 && (
        <div className={styles.rosPreview}>
          <p className={styles.rosPreviewTitle}>Day-of-show preview</p>
          <ul className={styles.rosPreviewRows}>
            {preview.map((r) => (
              <li key={`${r.sort}-${r.label}`} className={styles.rosPreviewRow}>
                <span className={styles.rosPreviewTime}>{r.time}</span>
                <span className={styles.rosPreviewLabel}>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Free-text production notes — the same `textareaLabel` pattern the VIP list and contract terms use,
          so the three read as one family rather than three different ideas of a textarea. */}
      <div className={styles.rosNotes}>
        <Textarea
          label="Load in & production"
          rows={3}
          placeholder={"Load In: 3:00 PM (All bands)\nBackline shared…"}
          value={seededNotes.loadInProduction}
          onChange={(e) => { setNotes({ ...seededNotes, loadInProduction: e.target.value }); setSaved(false); }}
        />
        <Textarea
          label="Load out notes"
          rows={3}
          placeholder="Load out immediately after final set…"
          value={seededNotes.loadOutNotes}
          onChange={(e) => { setNotes({ ...seededNotes, loadOutNotes: e.target.value }); setSaved(false); }}
        />
      </div>

      {unclaimed.length > 0 && (
        <p className={styles.help}>
          {unclaimed.length} other row{unclaimed.length === 1 ? "" : "s"} on this show are kept as saved.
        </p>
      )}
      <div className={styles.actions}>
        <SectionSave>
          <Button onClick={onSave} loading={update.isPending}>Save run of show</Button>
        </SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
        {/* A failed save used to render NOTHING here — the button just stopped spinning, which reads as a
            dead control rather than a rejected write. That is how the section's 400 went unnoticed for two
            days. Surface the server's reason, not a generic apology. */}
        {update.isError && (
          <span className={styles.savedNote} role="alert">
            {errorMessage(update.error, "Couldn't save the run of show.")}
          </span>
        )}
      </div>
    </div>
  );
}


// ---- Section 06: Setlist + Song Bank ---------------------------------------------------------------------

/** A private DataTransfer type for this list's row drags — `application/x-cntrl-setlist-song`. */
const SETLIST_DRAG_KIND = "setlist-song";

/**
 * One option in the Song Bank picker. `catalog` is a track off one of the band's releases (`trackId`),
 * `custom` is a title the band typed before (`customSongId`), and `new` is the not-found arm — the typed
 * text, offered as a song to create. The three write DIFFERENT columns on the setlist row, which is why the
 * pick carries its kind rather than just a title.
 */
type SongPick =
  | { kind: "catalog" | "custom"; songId: string; title: string }
  | { kind: "new"; title: string };

/** The set as it goes on the save wire — title + note, in order. Used to tell a real copy-from-last apart
 *  from the server's clean no-op, which returns the CURRENT setlist unchanged. */
function setlistFingerprint(items: readonly SetlistItemDto[]): string {
  return items.map((s) => `${s.title ?? ""} ${s.notes ?? ""}`).join("");
}

function SetlistSection({ showId }: { showId: string | undefined }) {
  const query = useSetlist(showId);
  const bank = useSongBank();
  const replace = useReplaceSetlist();
  const copyLast = useCopySetlistFromLast();
  const [items, setItems] = useState<SetlistItemDto[] | null>(null);
  const [newSong, setNewSong] = useState("");
  const [saved, setSaved] = useState(false);
  /** Copy-from-last is a full REPLACE that is also allowed to be a clean no-op, so it has to say which it
   *  was — the reference's `copyLabel`. Without it the button looks broken on the band's first show. */
  const [copyNote, setCopyNote] = useState("");

  const current: SetlistItemDto[] = items ?? query.data?.items ?? [];

  function edit(next: SetlistItemDto[]) { setItems(next); setSaved(false); }
  function removeRow(i: number) { edit(current.filter((_, idx) => idx !== i)); }
  /** Order IS the setlist — a set runs top to bottom, so moving a song is the primary edit here, not a
   *  nicety. `position` is re-derived from array order on save, so a move is purely a splice of this
   *  array and there is no index bookkeeping to drift out of step. */
  function move(from: number, to: number) {
    if (from === to || to < 0 || to >= current.length) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    edit(next);
  }

  function onCopyLast() {
    if (!showId) return;
    setCopyNote("Looking for your last set…");
    const before = setlistFingerprint(current);
    copyLast.mutate(showId, {
      onSuccess: (res) => {
        const next = res.items ?? [];
        setItems(next);
        setSaved(false);
        // The server RETURNS THE CURRENT SETLIST when no prior show has one, so identical bytes back is
        // the "nothing to copy" signal — the endpoint carries no source label of its own.
        setCopyNote(setlistFingerprint(next) === before
          ? "No previous show has a setlist yet."
          : `Copied ${next.length} song${next.length === 1 ? "" : "s"} from your last show.`);
      },
      onError: () => setCopyNote("Couldn’t reach your last show — try again."),
    });
  }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    const body = { items: current.map((i) => ({ title: i.title!, source: i.source, trackId: i.trackId, customSongId: i.customSongId, notes: i.notes })) };
    replace.mutate({ showId, body }, { onSuccess: (res) => { setItems(res.items ?? []); setSaved(true); } });
  }

  useSectionSave("setlist", {
    label: "Setlist",
    order: 6,
    isDirty: () => items !== null,
    save: async (id) => {
      await replace.mutateAsync({ showId: id, body: { items: current.map((i) => ({ title: i.title!, source: i.source, trackId: i.trackId, customSongId: i.customSongId, notes: i.notes })) } });
      setItems(null);
    },
  });

  // ---- the Song Bank picker ------------------------------------------------------------------------------
  // Both bank sources feed ONE list. `catalog` is every track on the band's releases (server-side, already
  // deduplicated by TITLE — a song on a single and again on the album is one song to a set); `custom` is what
  // this band has typed before. Catalog leads, because the band's own record is the likelier answer.
  const bankOptions: SongPick[] = [
    ...(bank.data?.catalog ?? []).map((s) => ({ kind: "catalog" as const, songId: s.songId!, title: s.title ?? "" })),
    ...(bank.data?.custom ?? []).map((s) => ({ kind: "custom" as const, songId: s.songId!, title: s.title ?? "" })),
  ].filter((o) => o.title.trim());

  const inSet = (title: string) => current.some((s) => (s.title ?? "").toLowerCase() === title.toLowerCase());

  /**
   * The picker's options for a query. Bank songs ALREADY IN THE SET are filtered out, so every row in the
   * dropdown does something — the set itself, directly above, is where you read what you already have. The
   * not-found arm is appended whenever the typed text isn't an exact match on a visible option, which is
   * also what keeps a deliberate reprise typeable: the title is filtered from the list, so "Add …" appears.
   */
  function suggestSongs(q: string): TypeaheadSuggestions<SongPick> {
    const term = q.trim();
    const lower = term.toLowerCase();
    const matches = bankOptions
      .filter((o) => !inSet(o.title))
      .filter((o) => !lower || o.title.toLowerCase().includes(lower));
    const exact = matches.some((o) => o.title.toLowerCase() === lower);
    return {
      items: term && !exact ? [...matches, { kind: "new", title: term }] : matches,
      // A cold bank degrades to a plain text box — never an error, and Add still works (R5).
      available: !bank.isLoading,
    };
  }

  function addPick(pick: SongPick) {
    const title = pick.title.trim();
    if (!title) return;
    // A typed title that already NAMES a bank song adds that song, not a second bank entry — otherwise a
    // reprise of a catalog track would mint a `custom_songs` row with the same title and the picker would
    // show the song twice from then on.
    const resolved = pick.kind !== "new"
      ? pick
      : bankOptions.find((o) => o.title.toLowerCase() === title.toLowerCase()) ?? pick;
    edit([...current, {
      itemId: undefined,
      position: current.length,
      title: resolved.kind === "new" ? title : resolved.title,
      source: resolved.kind === "catalog" ? "catalog" : "custom",
      trackId: resolved.kind === "catalog" ? resolved.songId : null,
      customSongId: resolved.kind === "custom" ? resolved.songId : null,
      notes: null,
    }]);
    setNewSong("");
  }

  if (query.isLoading) return <SectionLoading n={6} label="Setlist" />;
  if (query.isError) return <SectionError n={6} label="Setlist" query={query} />;

  return (
    <div className={styles.section}>
      {/* The section's one ACTION belongs beside its title, not in a strip of its own above the list —
          it seeds the list, so a row between the two read as a third thing. */}
      <div className={styles.sectionHead}>
        <SectionTitle n={6}>Setlist</SectionTitle>
        <Button variant="ghost" size="xs" loading={copyLast.isPending} disabled={!showId} onClick={onCopyLast}>
          Copy from last show
        </Button>
      </div>
      {/* ONLY the copy-from-last result — the one thing here that MOVES. The song count used to live on this
          line too and is gone: the list right below IS the count, so restating it only spent a line and the
          space around it. This renders nothing until a copy happens, so a normal set has none of it. */}
      {copyNote && (
        <p className={styles.help} role="status">{copyNote}</p>
      )}

      {current.length === 0 ? (
        // `inline`, not the centred block: this stands in for a ROW inside a dense section, and the
        // default's space-10 of block padding punches a hole that reads as broken rather than empty. Centred
        // horizontally (`.setlistEmpty`) because there is no list to sit flush-left against.
        <div className={styles.setlistEmpty}>
          <EmptyState variant="inline" title="No setlist yet" />
        </div>
      ) : (
        <ul className={styles.songList}>
          {current.map((row, i) => (
            <SetlistRow
              key={row.itemId ?? `new-${i}`}
              row={row}
              index={i}
              count={current.length}
              onMove={move}
              onNote={(value) => edit(current.map((r, idx) => (idx === i ? { ...r, notes: value } : r)))}
              onRemove={() => removeRow(i)}
            />
          ))}
        </ul>
      )}

      {/* ONE control, not a chip wall over a text field. The chip list only ever held the band's CUSTOM songs
          (the catalog half was empty), so it grew with every one-off cover while the actual catalogue was
          nowhere — and a band with a real back catalogue would have made it unscannable. A typeahead reads a
          long list, and its not-found arm is the "add your own" box the field below used to be. */}
      <form className={styles.quickAdd} onSubmit={(e) => { e.preventDefault(); addPick({ kind: "new", title: newSong }); }}>
        <div className={styles.quickAddField}>
        <TypeaheadInput<SongPick>
          label="Add a song"
          placeholder="Search your catalog"
          value={newSong}
          onChange={setNewSong}
          onPick={addPick}
          useSuggestions={suggestSongs}
          // An ADDER, not a stored value: a pick empties the box so the next song can be typed straight in.
          itemText={() => ""}
          itemKey={(o, n) => (o.kind === "new" ? `new-${n}` : `${o.kind}-${o.songId}`)}
          itemPrimary={(o) => (o.kind === "new" ? `Add “${o.title}”` : o.title)}
          // Catalog songs carry NO sub-line (Ryan, ART-210 — the title is enough); a one-off still labels
          // where it came from.
          itemSecondary={(o) =>
            o.kind === "catalog" ? null
            : o.kind === "custom" ? "Your Song Bank"
            : "Custom song"}
          // Both 0, against the vendor defaults (2 chars, 275ms): this source is already in memory, so the
          // list is a BROWSE as much as a search — focusing the empty box shows the whole catalogue — and a
          // debounce over an in-memory list is latency bought for nothing.
          minLength={0}
          debounceMs={0}
        />
        </div>
        <AddButton icon={Music} disabled={!newSong.trim()} />
      </form>
      {bank.data && !bank.data.catalogAvailable && (
        <p className={styles.help}>Your release catalog isn’t available right now — you can still type any title.</p>
      )}

      <div className={styles.actions}>
        <SectionSave><Button onClick={onSave} loading={replace.isPending}>Save setlist</Button></SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}

/**
 * One song, on ONE LINE: grip · position · title · note · remove.
 *
 * The grip is the drag source AND the keyboard control (the house reorder pattern — COMPONENTS.md
 * "Reordering — the one pattern"), which is what let the ↑/↓ pair go. `draggable` deliberately does NOT sit
 * on the row: it carries a text input, and a draggable row fights text selection.
 */
function SetlistRow({ row, index, count, onMove, onNote, onRemove }: {
  row: SetlistItemDto;
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onNote: (value: string) => void;
  onRemove: () => void;
}) {
  const drop = useDropTarget({ index, kind: SETLIST_DRAG_KIND, onMove });
  const name = (row.title ?? "").trim() || `song ${index + 1}`;
  return (
    <li className={styles.songRow} {...drop}>
      <DragHandle label={name} index={index} count={count} kind={SETLIST_DRAG_KIND} onMove={onMove} />
      <span className={styles.songPos}>{String(index + 1).padStart(2, "0")}</span>
      <span className={styles.songTitle}>
        <span className={styles.songTitleText}>{row.title}</span>
        {row.source === "custom" && <span className={styles.songSource}>custom</span>}
      </span>
      {/* A per-song note ("acoustic", "drop D") — the field the reference has beside every row. It rode the
          DTO from the start and nothing rendered it, so a note could be saved by nobody and read by nobody. */}
      <Field
        label={`Note for ${name}`}
        hideLabel
        placeholder="note · e.g. acoustic, drop D"
        className={styles.songNote}
        density="compact"
        value={row.notes ?? ""}
        onChange={(e) => onNote(e.target.value)}
      />
      <IconButton icon={Trash2} label={`Remove ${name}`} tone="danger" size="field" onClick={onRemove} />
    </li>
  );
}

// ---- Section 07: VIP -------------------------------------------------------------------------------------

function VipSection({ showId }: { showId: string | undefined }) {
  const query = useVip(showId);
  const update = useUpdateVip();
  const [seeded, setSeeded] = useState(false);
  const [entryTime, setEntryTime] = useState("");
  const [source, setSource] = useState("");
  const [list, setList] = useState("");
  const [saved, setSaved] = useState(false);
  /** VIP seeds three plain strings rather than a null draft, so it tracks dirty explicitly. */
  const [dirty, setDirty] = useState(false);

  // Seed local state once from the server.
  if (!seeded && query.data) {
    setEntryTime(query.data.entryTime ?? "");
    setSource(query.data.source ?? "");
    setList(query.data.list ?? "");
    setSeeded(true);
  }

  /** The VIP payload — shared by this section's own Save and the footer's fan-out. */
  function vipBody() {
    return {
      entryTime: entryTime ? (entryTime.length === 5 ? `${entryTime}:00` : entryTime) : null,
      source: source.trim() || null,
      list: list.trim() || null,
    };
  }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    update.mutate({ showId, body: vipBody() }, { onSuccess: () => { setSaved(true); setDirty(false); } });
  }

  useSectionSave("vip", {
    label: "VIP",
    order: 7,
    isDirty: () => dirty,
    save: async (id) => { await update.mutateAsync({ showId: id, body: vipBody() }); setDirty(false); },
  });

  if (query.isLoading) return <SectionLoading n={7} label="VIP" />;
  if (query.isError) return <SectionError n={7} label="VIP" query={query} />;

  return (
    <div className={styles.section}>
      <SectionTitle n={7}>VIP</SectionTitle>
      <div className={styles.grid}>
        <Field label="Entry time" type="time" value={entryTime.slice(0, 5)} onChange={(e) => { setEntryTime(e.target.value); setSaved(false); setDirty(true); }} />
        <Field label="Source (optional)" placeholder="e.g. Venue meet-and-greet" value={source} onChange={(e) => { setSource(e.target.value); setSaved(false); setDirty(true); }} />
      </div>
      <Textarea
        label="VIP list"
        rows={4}
        placeholder="One per line: Name, Name, email@example.com — or paste a CSV column."
        value={list}
        onChange={(e) => { setList(e.target.value); setSaved(false); setDirty(true); }}
      />
      <div className={styles.actions}>
        <SectionSave><Button onClick={onSave} loading={update.isPending}>Save VIP</Button></SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}

// ---- Section 09: Financials (MONEY — planned money, money:full | money:per_show) -----------------

// ART-241 — the deal ONLY. The road-cost buckets (hotel/flights/gas/rental/photo·video/foh) and member pay
// moved OUT of this section: costs are custom lines + travel in section 09 Expenses, and member pay is the
// locked top of that same section. The `show_finances` columns for the buckets are kept (inert) and carried
// forward untouched by this section's save; a migration converted legacy bucket data to expense lines.
type FinForm = {
  guarantee: string;
  ticketPrice: string; deposit: string; depositReceivedOn: string;
  agentPct: string; managerPct: string; doorSplit: boolean; doorSplitPct: string; doorSplitAfter: string;
  merchCut: boolean; merchCutPct: string; contractTerms: string;
};

function centsToStr(c: number | null | undefined): string { return c == null ? "" : (c / 100).toFixed(2); }
function strToCents(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Math.round(Number(t) * 100); }
function pctToStr(p: number | null | undefined): string { return p == null ? "" : String(p); }
function strToPct(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Number(t); }

function FinancialsSection({
  showId,
  seedGuaranteeCents = null,
  seedDoorSplitPct = null,
  mobile = false,
}: {
  /** The phone editor's Terms (ART-358): the deal type, the guarantee or door %, the two commissions, and the
   *  defaults option as a checkbox the Save honours. Every other field rides the full-replace PUT unchanged. */
  mobile?: boolean;
  showId: string | undefined;
  /** The offered figure, on a CREATE seeded from a booking offer — see {@link ShowCreateSeed}. Null otherwise.
   *  The caller nulls it in edit mode, so this section never has to know about the seed's edit rule. */
  seedGuaranteeCents?: number | null;
  /** The door share on a TERMS offer, same rules. It TICKS `doorSplit` as well as filling the percentage:
   *  `financialsBody` writes the percentage only when the box is on, so a seeded number with the box off
   *  would be discarded by the very save that creates the show. */
  seedDoorSplitPct?: number | null;
}) {
  const canFull = useCapability("money:full");
  const canPerShow = useCapability("money:per_show");
  const canMoney = canFull || canPerShow;   // unconditional reads (Rules of Hooks)
  const query = useFinancials(showId, canMoney);
  // GET /commissions is money:full ONLY — a per_show seat gating it on canMoney would fire a 403.
  const commissions = useCommissions(canFull);
  const saveDefaults = useUpdateCommissions();
  const update = useUpdateFinancials();
  // DEPOSIT lives on the SETTLEMENT, not financials — the one field in this section that does, and the
  // reference calls it out explicitly. So the section reads and writes that resource too; a save touches it
  // only when the deposit actually changed, so a plain financials edit stays one request.
  const settlement = useSettlement(showId, canMoney);
  const updateSettlement = useUpdateSettlement();
  const [form, setForm] = useState<FinForm | null>(null);
  const [saved, setSaved] = useState(false);
  const [defaultsSaved, setDefaultsSaved] = useState(false);
  // The phone editor's "Save agent / manager % as defaults" is a checkbox committed by Save, not a footer link.
  const [saveAsDefaults, setSaveAsDefaults] = useState(false);

  // The seed and the payload builders sit ABOVE the gates below, because `useSectionSave` is a HOOK and has
  // to run on every render — including the money-gated and still-loading ones. Nothing here calls a hook or
  // reads a value the gates protect: with no data yet `d` is undefined and `f` seeds blank, which is only
  // ever *read* through inputs that the loading gate has already stopped from rendering. The freeze the
  // comment below describes happens on `setForm`, not here.
  const d = query.data?.data;
  const version = query.data?.version ?? 0;
  const f: FinForm = form ?? {
    guarantee: centsToStr(d?.guaranteeCents ?? seedGuaranteeCents),
    ticketPrice: centsToStr(d?.ticketPriceCents),
    deposit: centsToStr(settlement.data?.data?.depositCents),
    depositReceivedOn: settlement.data?.data?.depositReceivedOn ?? "",
    // Agent/manager % default from the band's commission defaults when the plan has none yet.
    agentPct: pctToStr(d?.agentPct ?? commissions.data?.agentPct),
    managerPct: pctToStr(d?.managerPct ?? commissions.data?.managerPct),
    doorSplit: d?.doorSplit ?? seedDoorSplitPct != null,
    doorSplitPct: pctToStr(d?.doorSplitPct ?? seedDoorSplitPct),
    doorSplitAfter: centsToStr(d?.doorSplitAfterCents),
    merchCut: d?.merchCut ?? false, merchCutPct: pctToStr(d?.merchCutPct), contractTerms: d?.contractTerms ?? "",
  };
  /** The financials PUT body — shared by this section's own Save and the footer's fan-out. The section is now
   *  the DEAL only: the six road-cost buckets, the per-diem rate and member pay are all carried through the
   *  full-replace UNCHANGED (their stored values), because their editors moved elsewhere (07 Travel for the
   *  per-diem rate, section 09 Expenses for costs + member pay) and a full-replace that omitted them would
   *  null-wipe what those sections wrote. */
  function financialsBody() {
    return {
      guaranteeCents: strToCents(f.guarantee),
      // Everything below the deal is carried forward from the stored plan (this section no longer edits it):
      perPersonCents: d?.perPersonCents ?? undefined,
      // The buckets are inert (a migration converted legacy values to expense lines) but still carried so this
      // save never blanks a value some other path wrote; the per-diem rate is 07 Travel's.
      hotelCents: d?.hotelCents ?? null, perDiemCents: d?.perDiemCents ?? null,
      flightsCents: d?.flightsCents ?? null, gasCents: d?.gasCents ?? null,
      rentalCents: d?.rentalCents ?? null, photoVideoCents: d?.photoVideoCents ?? null,
      fohCents: d?.fohCents ?? null,
      ticketPriceCents: strToCents(f.ticketPrice),
      agentPct: strToPct(f.agentPct), managerPct: strToPct(f.managerPct),
      doorSplit: f.doorSplit, doorSplitPct: f.doorSplit ? strToPct(f.doorSplitPct) : undefined,
      doorSplitAfterCents: f.doorSplit ? strToCents(f.doorSplitAfter) : undefined,
      merchCut: f.merchCut, merchCutPct: f.merchCut ? strToPct(f.merchCutPct) : undefined,
      contractTerms: f.contractTerms.trim() === "" ? undefined : f.contractTerms.trim(),
      // Member pay is section 09's now, via its own endpoint — `undefined` LEAVES the stored lines so this
      // deal save never wipes them (the two sections would otherwise each null-clobber the other).
      memberPay: undefined,
    };
  }

  /**
   * The DEPOSIT half of a save, or `null` when it did not change. The deposit lives on the SETTLEMENT, not
   * on financials, and a settlement PUT bumps that record's version and is audited — so a financials-only
   * edit must not touch it.
   *
   * The received stamp is in the CHANGED test too: marking a deposit received without touching its amount
   * would otherwise be silently dropped, because the settlement PUT would never fire.
   */
  function depositWrite() {
    const depositCents = strToCents(f.deposit) ?? null;
    const storedDeposit = settlement.data?.data?.depositCents ?? null;
    const receivedOn =
      (strToCents(f.deposit) ?? 0) <= 0 || f.depositReceivedOn.trim() === "" ? null : f.depositReceivedOn;
    const storedReceivedOn = settlement.data?.data?.depositReceivedOn ?? null;
    if (depositCents === storedDeposit && receivedOn === storedReceivedOn) return null;

    const s = settlement.data?.data;
    return {
      version: settlement.data?.version ?? 0,
      body: {
        presaleSold: s?.presaleSold, presalePriceCents: s?.presalePriceCents,
        walkupSold: s?.walkupSold, walkupPriceCents: s?.walkupPriceCents,
        vipCount: s?.vipCount, vipPriceCents: s?.vipPriceCents,
        // The deal (guarantee / door split % / threshold) is NOT round-tripped here: it belongs to the
        // financials PUT, which is its one authority, and this PUT ignores it.
        merchGrossCents: s?.merchGrossCents,
        merchSoftGoodsPct: s?.merchSoftGoodsPct, merchRecordedPct: s?.merchRecordedPct,
        depositCents, depositReceivedOn: receivedOn,
        // Expenses/payouts are NOT sent: a null `expenses` leaves the stored lines alone (a full replace
        // would wipe them), and payouts are append-only.
        expenses: undefined, addPayouts: undefined,
      },
    };
  }

  /*
   * 09 Financials is the footer Save's, like every other section — and it was the ONE section that never
   * said so (ART-85). Inside the editor modal `SectionSave` renders NOTHING, because the ribbon carries a
   * single Save that fans out over the registry; a section missing from the registry therefore has its own
   * button suppressed and no share of the footer's, so it has no way to save AT ALL. Typed figures were
   * simply discarded when the modal closed, which is why the editor and the show preview disagreed about
   * the same show's money — the preview was reading a deal the editor had never been able to write.
   *
   * `form !== null` is the dirty test, the same null-draft signal 10 Custom Expenses uses: `form` is seeded
   * lazily and only `set()` ever populates it, so it is non-null exactly when a field has been typed into.
   * Both writes are AWAITED so a failure reaches the fan-out, which names the section rather than reporting
   * a save that did not happen.
   */
  useSectionSave("financials", {
    label: "Terms",
    order: 9,
    // `form !== null` is "somebody typed here". A SEEDED create has typed nothing, so on its own that test
    // would skip the flush and the offered figure would be dropped by the very save that creates the
    // show — silently, which is the failure this whole hand-off exists to avoid. A seed is a pending write.
    // BOTH halves of a terms deal count: an offer with a door split but no guarantee ("70% of the door") is
    // a real one, and testing only the guarantee would drop it.
    isDirty: () =>
      canMoney && (form !== null || saveAsDefaults || (!showId && (seedGuaranteeCents != null || seedDoorSplitPct != null))),
    save: async (id) => {
      if (form !== null || !showId) {
        await update.mutateAsync({ showId: id, version, body: financialsBody() });
        const deposit = depositWrite();
        if (deposit) await updateSettlement.mutateAsync({ showId: id, ...deposit });
      }
      if (saveAsDefaults && canFull) {
        await saveDefaults.mutateAsync({ agentPct: strToPct(f.agentPct) ?? null, managerPct: strToPct(f.managerPct) ?? null });
        setSaveAsDefaults(false);
      }
      setForm(null);
    },
  });

  // The read-only Schedule tier holds no money token — the section is money-gated by construction.
  if (!canMoney) {
    return (
      <div className={styles.section}>
        <SectionTitle n={8}>Terms</SectionTitle>
        <EmptyState title="Terms are money-gated" description="Your role doesn’t include financial access for this show." />
      </div>
    );
  }
  // BOTH resources gate the render, not just financials. `f` seeds `deposit` + `depositReceivedOn` from the
  // SETTLEMENT, and the first keystroke in any field freezes that seed into `form` — so rendering before the
  // settlement landed let a plain guarantee edit save `depositCents: null` over a deposit somebody had
  // recorded. The commission defaults feed `agentPct`/`managerPct` the same way.
  if (query.isLoading || settlement.isLoading || commissions.isLoading)
    return <SectionLoading n={8} label="Terms" />;
  if (query.isError) return <SectionError n={8} label="Terms" query={query} />;

  function set(p: Partial<FinForm>) { setForm({ ...f, ...p }); setSaved(false); setDefaultsSaved(false); }

  if (mobile) {
    return (
      <div className={styles.section}>
        <div className={styles.pillField}>
          <span className={styles.pillLegend} aria-hidden="true">Deal</span>
          <SegmentedControl
            label="Deal"
            value={f.doorSplit ? "door" : "guarantee"}
            onChange={(v) => set({ doorSplit: v === "door" })}
            options={[{ value: "guarantee", label: "Guarantee" }, { value: "door", label: "Door share" }]}
          />
        </div>
        {f.doorSplit ? (
          // A door deal here is the versus deal the model stores: the door % against the guarantee, so the
          // guarantee stays in view rather than being hidden by the switch.
          <div className={styles.mobilePair}>
            <Field label="Door %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.doorSplitPct} onChange={(e) => set({ doorSplitPct: e.target.value })} />
            <MoneyField label="Guarantee" value={f.guarantee} onChange={(v) => set({ guarantee: v })} />
          </div>
        ) : (
          <MoneyField label="Guarantee" value={f.guarantee} onChange={(v) => set({ guarantee: v })} />
        )}
        <div className={styles.mobilePair}>
          <Field label="Agent %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.agentPct} onChange={(e) => set({ agentPct: e.target.value })} />
          <Field label="Manager %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.managerPct} onChange={(e) => set({ managerPct: e.target.value })} />
        </div>
        {canFull && (
          <Checkbox
            className={styles.checkboxRow}
            label="Save agent / manager % as defaults"
            checked={saveAsDefaults}
            onChange={setSaveAsDefaults}
          />
        )}
      </div>
    );
  }

  // A deposit the deal does not ask for cannot have arrived: blank and `0.00` both mean "no deposit", and
  // both disable the received tick AND force its stamp to null on save.
  const hasDeposit = (strToCents(f.deposit) ?? 0) > 0;

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    update.mutate(
      { showId, version, body: financialsBody() },
      { onSuccess: () => { setForm(null); setSaved(true); } },
    );
    const deposit = depositWrite();
    if (deposit) updateSettlement.mutate({ showId, ...deposit });
  }

  return (
    <div className={styles.section}>
      <SectionTitle n={8}>Terms</SectionTitle>

      <div className={styles.grid}>
        {/* ART-241 — the DEAL only. Road costs (hotel/flights/gas/rental/photo·video/FOH) and member pay moved
            to section 09 Expenses; per-diem rate is 07 Travel's. What is left is the guarantee, the deposit,
            the ticket price, the commission %s, the door split, the merch cut and the contract terms. */}
        <MoneyField label="Guarantee" value={f.guarantee} onChange={(v) => set({ guarantee: v })} />
        {/* The deposit PAIR shares one grid cell: the agreed amount on the left half, whether it has arrived
            on the right. They are one fact in two parts — a figure owed and a yes/no — so splitting them
            across the two-column grid would put the tick beside an unrelated money box, where it reads as
            that box's label.

            A CHECKBOX, not a Switch: it is staged and committed by this section's Save like every other
            field here, and a Switch would claim it had already landed. Ticking stamps TODAY; an existing
            stamp is preserved, so correcting the amount never re-dates when the money arrived.

            Disabled AND unchecked while the amount is blank or 0.00 — there is no arrival to record for a
            deposit the deal does not ask for, and a tick beside $0.00 claims one. Disabled rather than
            hidden, so the pair keeps its shape as you type the amount in. `deposit` is also the ONE field in
            this section that writes to the settlement rather than financials. */}
        <div className={styles.depositPair}>
          <MoneyField label="Deposit" value={f.deposit} onChange={(v) => set({ deposit: v })} />
          <Checkbox
            className={styles.depositCheck}
            label="Deposit received"
            disabled={!hasDeposit}
            checked={hasDeposit && f.depositReceivedOn.trim() !== ""}
            onChange={(next) => set({ depositReceivedOn: next ? (f.depositReceivedOn || todayIso()) : "" })}
          />
        </div>
        <MoneyField label="Ticket price" value={f.ticketPrice} onChange={(v) => set({ ticketPrice: v })} />
      </div>

      <div className={styles.grid}>
        <Field label="Agent %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.agentPct} onChange={(e) => set({ agentPct: e.target.value })} />
        <Field label="Manager %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.managerPct} onChange={(e) => set({ managerPct: e.target.value })} />
      </div>

      <Checkbox
        className={styles.checkboxRow}
        label="Door split"
        checked={f.doorSplit}
        onChange={(next) => set({ doorSplit: next })}
      />
      {f.doorSplit && (
        <div className={styles.grid}>
          <Field label="Door split %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.doorSplitPct} onChange={(e) => set({ doorSplitPct: e.target.value })} />
          <MoneyField label="After (threshold)" value={f.doorSplitAfter} onChange={(v) => set({ doorSplitAfter: v })} />
        </div>
      )}

      <Checkbox
        className={styles.checkboxRow}
        label="Venue merch cut"
        checked={f.merchCut}
        onChange={(next) => set({ merchCut: next })}
      />
      {f.merchCut && (
        <div className={styles.grid}>
          <Field label="Merch cut %" type="number" inputMode="decimal" min="0" max="100" step="0.01" value={f.merchCutPct} onChange={(e) => set({ merchCutPct: e.target.value })} />
        </div>
      )}

      {/* The primitive renders its OWN label — the old wrapper would name the control twice. */}
      <Textarea label="Contract terms" rows={3} value={f.contractTerms} onChange={(e) => set({ contractTerms: e.target.value })} placeholder="Notable terms, riders, deposits…" />

      <div className={styles.actions}>
        <SectionSave><Button onClick={onSave} loading={update.isPending}>Save terms</Button></SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>

      {/* The reference's `★ Save agent / manager % as defaults` footer link — it writes the BAND's commission
          defaults (PUT /commissions, money:full), which is why it only appears for a full-money seat: the
          per_show seat that can edit this show's split has no authority over the band's default. The glyph is
          an Icon, not the spec's literal `★` (a listed spec defect, not a parity target). */}
      {canFull && (
        <EditorFooterSlot zone="extra">
          <button
            type="button"
            className={styles.defaultsLink}
            disabled={saveDefaults.isPending}
            onClick={() =>
              saveDefaults.mutate(
                { agentPct: strToPct(f.agentPct) ?? null, managerPct: strToPct(f.managerPct) ?? null },
                { onSuccess: () => setDefaultsSaved(true) },
              )
            }
          >
            <Icon icon={Star} size={16} />
            {defaultsSaved ? "Saved as defaults" : "Save agent / manager % as defaults"}
          </button>
        </EditorFooterSlot>
      )}
    </div>
  );
}

function MoneyField({ label, value, onChange, hideLabel = false }: { label: string; value: string; onChange: (v: string) => void; hideLabel?: boolean }) {
  // Field renders its own <input> — pass props directly (never wrap an input as children).
  return <Field label={label} hideLabel={hideLabel} type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={value} onChange={(e) => onChange(e.target.value)} />;
}

// ---- Section 9: Expenses ─ member pay (locked) + travel (display-only) + custom lines (ART-241) -----------
//
// ONE list of three row kinds, and the data model keeps them SEPARATE tables (they are treated differently —
// the settle box, the money gates — so a unified entity would just re-split what it merged):
//   • MEMBER PAY (top, LOCKED): `show_member_pay`, money:full|per_show. The roster drives the rows; you set an
//     amount, you cannot add/remove/rename/reorder, and there is no reimbursement flag (pay always settles).
//     Written by its OWN endpoint (useSetMemberPay) so it never null-wipes the deal section 08 owns.
//   • TRAVEL (display-only): a projection of `show_travel_items` legs that carry a cost (money:travel — a
//     schedule seat sees no cost block, so no travel rows). Read-only label + amount; the ONE editable control
//     is "needs reimbursement" (a targeted PATCH). The cost lives in section 07 Travel; this only dispositions it.
//   • CUSTOM (fully editable): `show_expense_lines`, schedule-tier — label + amount + needs-reimbursement.
// The section is NOT money-gated as a whole (custom lines are schedule-tier); the money halves gate internally.

/** A travel leg's label where it surfaces as an expense — mirrors the server's Travel.TravelLabels.ForLeg. */
function travelLegLabelFrom(kind: string, airline?: string | null, flightNumber?: string | null, property?: string | null): string {
  if (kind === "hotel") return property ? `Hotel: ${property}` : "Hotel";
  if (kind === "ground") return "Ground transport";
  const air = airline ?? "";
  return air ? (flightNumber ? `Flight: ${air} ${flightNumber}` : `Flight: ${air}`) : "Flight";
}
function travelLegLabel(i: TravelItemDto): string {
  return travelLegLabelFrom(i.kind ?? "flight", i.airline, i.flightNumber, i.propertyName);
}

function CustomExpensesSection({ showId }: { showId: string | undefined }) {
  const query = useExpenses(showId);
  const update = useUpdateExpenses();
  const [rows, setRows] = useState<ExpenseLineDraft[] | null>(null);
  const [saved, setSaved] = useState(false);

  // ── Member pay (money-gated) ──────────────────────────────────────────────────────────────────────
  // Both capability hooks unconditionally (Rules of Hooks), then combine.
  const canFullMoney = useCapability("money:full");
  const canPerShowMoney = useCapability("money:per_show");
  const canMoney = canFullMoney || canPerShowMoney;
  const fin = useFinancials(showId, canMoney);
  const setMemberPayM = useSetMemberPay();
  const members = useShowMembers(showId);
  const nameOf = useMemberNames();
  // The editor's LIVE roster (staged additions included) when inside the editor, else the saved server list.
  const editorRoster = useEditorRosterValue();
  const serverMemberIds = (members.data?.items ?? []).map((m) => m.personId).filter((id): id is string => !!id);
  const rosterKnown = editorRoster !== null || !!members.data;
  const payMemberIds = editorRoster ?? serverMemberIds;
  const [payDraft, setPayDraft] = useState<Record<string, string> | null>(null);
  const storedPay = new Map<string, number>();
  for (const l of fin.data?.data?.memberPay ?? []) if (l.personId) storedPay.set(l.personId, l.amountCents ?? 0);
  const payFor = (id: string): string =>
    payDraft?.[id] ?? (storedPay.has(id) ? centsToStr(storedPay.get(id)!) : "");
  function setPay(id: string, amount: string) { setPayDraft({ ...(payDraft ?? {}), [id]: amount }); setSaved(false); }

  // ── Travel-derived rows (display-only; money:travel via cost presence) ─────────────────────────────
  // Prefer the LIVE editor draft (a cost typed in section 07 shows here BEFORE any save); fall back to the
  // SAVED travel when mounted standalone (no editor provider — e.g. unit tests). In the live case the toggle
  // writes back into section 07's draft (persisted by its travel save); in the fallback it PATCHes the saved leg.
  const liveTravel = useEditorTravelValue();
  const travel = useTravel(showId);
  const setReimbM = useSetTravelReimbursement();
  const [travelDraft, setTravelDraft] = useState<Record<string, boolean> | null>(null);
  function setReimbSaved(id: string, v: boolean) { setTravelDraft({ ...(travelDraft ?? {}), [id]: v }); setSaved(false); }
  const travelRows: { key: string; label: string; costCents: number; needsReimbursement: boolean; onToggle: (v: boolean) => void }[] =
    liveTravel
      ? liveTravel.items
          .filter((i) => (i.costCents ?? 0) > 0)
          .map((i) => ({
            key: i.localKey,
            label: travelLegLabelFrom(i.kind, i.airline, i.flightNumber, i.propertyName),
            costCents: i.costCents!,
            needsReimbursement: i.needsReimbursement,
            onToggle: (v: boolean) => liveTravel.setNeedsReimbursement(i.localKey, v),
          }))
      : (travel.data?.items ?? [])
          .filter((i): i is TravelItemDto & { travelItemId: string } => !!i.travelItemId && (i.cost?.costCents ?? 0) > 0)
          .map((i) => ({
            key: i.travelItemId,
            label: travelLegLabel(i),
            costCents: i.cost!.costCents!,
            needsReimbursement: travelDraft?.[i.travelItemId] ?? i.needsReimbursement ?? true,
            onToggle: (v: boolean) => setReimbSaved(i.travelItemId, v),
          }));

  // ── Linked days (ART-240): the off/travel days attributed to this show roll INTO the Travel list as ONE
  // read-only "Per diems" row (their total — day pay + per diem), ALWAYS settled, so no reimbursement toggle.
  // They live in `tour_days` (edited in the day editor), so this only DISPLAYS them; the money is show pay,
  // hence the `canMoney` gate, not the legs' money:travel. ─────────────────────────────────────────────────
  const linked = useShowLinkedDays(showId, canMoney);
  const linkedDaysCents = canMoney ? (linked.data?.totalCents ?? 0) : 0;

  // ── Custom lines ──────────────────────────────────────────────────────────────────────────────────
  const current: ExpenseLineDraft[] =
    rows ?? (query.data?.items ?? []).map((l: ExpenseLineDto) => ({
      label: l.label ?? "", amount: dollars(l.amountCents), needsReimbursement: l.needsReimbursement ?? true,
      lineId: l.lineId, addedByName: l.addedByName ?? null, hasReceipt: !!l.receiptMediaId,
    }));

  function edit(next: ExpenseLineDraft[]) { setRows(next); setSaved(false); }

  // The three writers, one per table. Custom lines full-replace; member pay full-replaces its own endpoint
  // (only non-zero pays); each changed travel reimbursement flag is a targeted PATCH.
  async function saveExpenses(id: string) {
    await update.mutateAsync({ showId: id, body: { items: current.filter((r) => r.label.trim() !== "").map((r) => ({ label: r.label.trim(), amountCents: strToCents(r.amount) ?? 0, needsReimbursement: r.needsReimbursement, lineId: r.lineId ?? null })) } });
    setRows(null);
  }
  async function savePay(id: string) {
    if (!rosterKnown) return;   // never wipe pay on a save that could not see the roster
    await setMemberPayM.mutateAsync({
      showId: id,
      body: {
        members: payMemberIds
          .map((pid) => ({ personId: pid, amountCents: strToCents(payFor(pid)) }))
          .filter((l): l is { personId: string; amountCents: number } => l.amountCents != null && l.amountCents > 0),
      },
    });
    setPayDraft(null);
  }
  async function saveTravel(id: string) {
    for (const [travelItemId, needsReimbursement] of Object.entries(travelDraft ?? {}))
      await setReimbM.mutateAsync({ showId: id, travelItemId, needsReimbursement });
    setTravelDraft(null);
  }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    // The standalone button always posts the custom lines (a full replace of the current state), then the
    // money halves only when they were touched — mirroring the section's pre-ART-241 Save.
    void (async () => {
      await saveExpenses(showId);
      if (payDraft !== null) await savePay(showId);
      if (travelDraft !== null) await saveTravel(showId);
      setSaved(true);
    })().catch(() => { /* the hooks toast on error */ });
  }

  // The footer fan-out is dirty-gated per part, so an unrelated section's Save does not re-PUT this one.
  useSectionSave("expenses", {
    label: "Expenses",
    order: 10,
    isDirty: () => rows !== null || payDraft !== null || travelDraft !== null,
    save: async (id) => {
      if (rows !== null) await saveExpenses(id);
      if (payDraft !== null) await savePay(id);
      if (travelDraft !== null) await saveTravel(id);
    },
  });

  if (query.isLoading) return <SectionLoading n={9} label="Expenses" />;
  if (query.isError) return <SectionError n={9} label="Expenses" query={query} />;

  const anyPending = update.isPending || setMemberPayM.isPending || setReimbM.isPending;

  return (
    <div className={styles.section}>
      <SectionTitle n={9}>Expenses</SectionTitle>

      {/* Member pay — locked rows at the top (non-custom). Money-gated; a schedule seat never sees it. */}
      {canMoney && (
        <div className={styles.memberPay}>
          <div className={styles.memberPayHead}>Member pay</div>
          {payMemberIds.length === 0 ? (
            <p className={styles.hint}>Add crew in the Members section and each one becomes a pay line here.</p>
          ) : (
            payMemberIds.map((id) => {
              const name = nameOf({ personId: id }).name;
              return (
                <div key={id} className={styles.memberPayRow}>
                  <span className={styles.memberPayName}>{name}</span>
                  <MoneyField label={`Pay — ${name}`} hideLabel value={payFor(id)} onChange={(v) => setPay(id, v)} />
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Travel — display-only projection of costed legs (the only editable control is the reimbursement flag)
          plus a single read-only "Per diems" row rolling in the ART-240 linked days: always settles, so no
          toggle. */}
      {(travelRows.length > 0 || linkedDaysCents > 0) && (
        <div className={styles.memberPay}>
          <div className={styles.memberPayHead}>Travel</div>
          <ul className={styles.slots}>
            {travelRows.map((t) => (
              <li key={t.key} className={styles.travelExpenseRow}>
                <span className={styles.travelExpenseLabel}>{t.label}</span>
                <span className={styles.travelExpenseAmount}>${(t.costCents / 100).toFixed(2)}</span>
                <label className={styles.reimburseToggle} title="Show this on the settle-up page. Uncheck for a recorded cost that needs no reimbursement.">
                  <input type="checkbox" checked={t.needsReimbursement} onChange={(e) => t.onToggle(e.target.checked)} />
                  Needs reimbursement
                </label>
              </li>
            ))}
            {linkedDaysCents > 0 && (
              <li className={styles.travelExpenseRow}>
                <span className={styles.travelExpenseLabel}>Per diems</span>
                <span className={styles.travelExpenseAmount}>${(linkedDaysCents / 100).toFixed(2)}</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Custom expenses — fully editable rows. Wrapped in the tight `.memberPay` block (head + rows at
          space-2) so the subhead hugs its rows the way Member pay and Travel do, not the wider section gap. */}
      <div className={styles.memberPay}>
        <div className={styles.memberPayHead}>Custom expenses</div>
        <ExpenseLinesList rows={current} onChange={edit} addLabel="Add expense" />
      </div>
      <div className={styles.actions}>
        <SectionSave><Button onClick={onSave} loading={anyPending}>Save expenses</Button></SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}

function dollars(cents: number | undefined | null): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}
function parseDollars(v: string): number | null {
  const n = parseFloat(v);
  return v && Number.isFinite(n) ? Math.round(n * 100) : null;
}

// ---- Section 08: Travel ---------------------------------------------------------------------------

type TravelItemDto = components["schemas"]["TravelItemDto"];
type ParsedTravelItem = components["schemas"]["ParsedTravelItem"];
type TravelRow = TravelItemDto & { passengerName?: string | null; localKey: string };

const TRAVEL_KINDS = [
  { value: "flight", label: "Flight" },
  { value: "hotel", label: "Hotel" },
  { value: "ground", label: "Ground" },
] as const;

let travelRowSeq = 0;
const newTravelKey = () => `t${travelRowSeq++}`;

function toTravelRow(i: TravelItemDto): TravelRow {
  return { ...i, personIds: i.personIds ?? [], localKey: newTravelKey() };
}
function parsedToTravelRow(p: ParsedTravelItem): TravelRow {
  const { suggestedPersonIds, ...rest } = p;
  return { ...rest, travelItemId: null, personIds: suggestedPersonIds ?? [], localKey: newTravelKey() };
}
function travelRowHasContent(r: TravelRow): boolean {
  return !!(r.flightNumber || r.airline || r.departAirport || r.arriveAirport || r.propertyName
    || r.address || r.confirmationCode || r.checkIn || r.checkOut || r.room || r.seat);
}
function travelRowToWire(r: TravelRow): TravelItemDto {
  const { localKey: _k, passengerName: _p, ...item } = r;
  return item;
}
function fileToDataUrl(file: File): Promise<{ imageBase64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => resolve({ imageBase64: String(reader.result), mimeType: file.type || "image/jpeg" });
    reader.readAsDataURL(file);   // a data: URI — the server strips the prefix
  });
}

// ---- Section 08 display + assignment helpers ------------------------------------------------------

/** A saved leg's wall-clock date (`2020-09-19T09:30:00` → `2020-09-19`). */
function localDate(s: string | null | undefined): string { return s ? s.slice(0, 10) : ""; }
/** …and its time part in 24h (`…T09:30:00` → `09:30`), for a native `<input type="time">`. */
function localTime(s: string | null | undefined): string { return s ? s.slice(11, 16) : ""; }
/** 12-hour display of a wall-clock stamp (`09:30` → `9:30 AM`), for the compact sub-line. */
function displayTime(s: string | null | undefined): string {
  const t = localTime(s);
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}
/** Recombine a date + time pair into the wall-clock string the wire carries (time optional → midnight). */
function combineLocal(date: string, time: string): string | null {
  if (!date) return null;
  return `${date}T${time || "00:00"}:00`;
}

const TRAVEL_TAGS: Record<string, string> = { flight: "FLIGHT", hotel: "HOTEL", ground: "GROUND" };

function joinDot(parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" · ");
}

/** The compact display row's headline + one dot-separated sub-line, from the fields we already hold. */
function travelSummary(r: TravelRow): { tag: string; title: string; sub: string } {
  const tag = TRAVEL_TAGS[r.kind ?? "flight"] ?? "LEG";
  if (r.kind === "hotel") {
    const nights = [r.checkIn, r.checkOut].filter(Boolean).join(" → ");
    return {
      tag,
      title: r.propertyName || "Hotel",
      sub: joinDot([nights, r.room && `room ${r.room}`, r.confirmationCode && `conf ${r.confirmationCode}`]),
    };
  }
  const route = r.departAirport && r.arriveAirport ? `${r.departAirport} → ${r.arriveAirport}` : (r.departAirport || r.arriveAirport || "");
  const times = displayTime(r.departLocal) && displayTime(r.arriveLocal)
    ? `${displayTime(r.departLocal)} - ${displayTime(r.arriveLocal)}`
    : (displayTime(r.departLocal) || displayTime(r.arriveLocal));
  return {
    tag,
    title: joinDot([r.airline, r.flightNumber]) || (r.kind === "ground" ? "Ground" : "Flight"),
    sub: joinDot([route, localDate(r.departLocal), times, r.seat && `seat ${r.seat}`, r.confirmationCode && `conf ${r.confirmationCode}`]),
  };
}

type Assignment = { assignedAll: boolean; personIds: string[] };

/** The closed dropdown's label. Every member selected reads "Everyone" (PO) just like the shared-party flag. */
function assigneeLabel(a: Assignment, members: MemberDto[], nameOf: (m: MemberDto) => { name: string }): string {
  const ids = a.personIds ?? [];
  if (a.assignedAll) return "Everyone";
  if (ids.length === 0) return "Unassigned";
  const memberIds = members.map((m) => m.personId).filter((x): x is string => !!x);
  if (memberIds.length > 0 && memberIds.every((id) => ids.includes(id))) return "Everyone";
  if (ids.length === 1) {
    const m = members.find((x) => x.personId === ids[0]);
    return m ? nameOf(m).name : "1 person";
  }
  return `${ids.length} people`;
}

/**
 * The "assigned to" control — "Everyone" (a single reservation covers the whole party) OR any of the show's
 * members, multi-select. The same control sits on the compact row (quick reassign) and in the edit card's
 * header. Multi-select, so it STAYS open on a toggle and dismisses on outside-press / Escape.
 */
function AssigneeMenu({ value, members, nameOf, onChange, align = "end" }: {
  value: Assignment;
  members: MemberDto[];
  nameOf: (m: MemberDto) => { name: string };
  onChange: (next: Assignment) => void;
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useMenuDismiss(open, () => setOpen(false), rootRef, triggerRef);
  const ids = value.personIds ?? [];
  function toggle(id: string) {
    const on = !value.assignedAll && ids.includes(id);
    onChange({ assignedAll: false, personIds: on ? ids.filter((p) => p !== id) : [...ids.filter((p) => p !== id), id] });
  }
  return (
    <div className={styles.assignee} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.assigneeTrigger}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{assigneeLabel(value, members, nameOf)}</span>
        <Icon icon={ChevronDown} size={16} />
      </button>
      {open && (
        <div className={styles.assigneeMenu} role="menu" data-align={align}>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={value.assignedAll}
            className={styles.assigneeItem}
            data-on={value.assignedAll}
            onClick={() => onChange({ assignedAll: true, personIds: [] })}
          >
            <Icon icon={Check} size={16} className={styles.assigneeCheck} /> Everyone
          </button>
          {members.map((m) => {
            const id = m.personId!;
            const on = !value.assignedAll && ids.includes(id);
            return (
              <button
                key={id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                className={styles.assigneeItem}
                data-on={on}
                onClick={() => toggle(id)}
              >
                <Icon icon={Check} size={16} className={styles.assigneeCheck} /> {nameOf(m).name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TravelSection({ showId }: { showId: string | undefined }) {
  const query = useTravel(showId);
  const members = useShowMembers(showId);
  const parse = useParseItinerary();
  const save = useSaveTravel();
  // ART-240 — the per-diem RATE (money-gated) lives here now, its one editor. It writes show_finances through
  // this section's save (09 Financials sources the stored value, so it never blanks it). And the money-gated
  // day-cost split (per-diem + pay per day) rides the show's linked-days read.
  const canFullMoney = useCapability("money:full");
  const canPerShowMoney = useCapability("money:per_show");
  const canMoney = canFullMoney || canPerShowMoney;
  // A leg's COST is its own money category (money:travel), separate from the per-diem/deal money above. Gate
  // the Cost field on the CAPABILITY, not on `cost != null`: a freshly ADDED leg has no cost block yet, so the
  // presence test hid the field on every hand-entered leg and there was no way to enter a cost at all.
  const canTravelMoney = useCapability("money:travel");
  const fin = useFinancials(showId, canMoney);
  const updateFin = useUpdateFinancials();
  const linkedDayCosts = useShowLinkedDays(showId, canMoney);
  const runs = useRuns();
  const [perDiem, setPerDiem] = useState<string | null>(null);
  const [perDiemDirty, setPerDiemDirty] = useState(false);
  const [rows, setRows] = useState<TravelRow[] | null>(null);
  /** Whether the last parse failed. It briefly had a third state — "there is no show to parse against" —
   *  which was a silent `return` before that (*"it just silently does nothing"*, 2026-09-08, reported on a
   *  NEW show where `showId` is undefined until Create show). Both are gone: the parse runs against the BAND
   *  when there is no show, so the case no longer exists rather than being reported better. */
  const [parseFailed, setParseFailed] = useState(false);
  /** Covers the WHOLE operation, not just the request: a dropped file is read to a data URL first, and
   *  `parse.isPending` is false for that stretch — so the button sat inert for the part of the wait the
   *  user actually notices. */
  const [parsing, setParsing] = useState(false);
  const [saved, setSaved] = useState(false);
  /** Travel seeds its rows from the server in an effect, so `rows !== null` is NOT a dirty signal the way
   *  it is in 10 Custom Expenses — it tracks dirty explicitly instead, the way 07 VIP does. */
  const [dirty, setDirty] = useState(false);
  /** Which leg is open in the edit card, with a working DRAFT so Cancel discards and the committed row stays
   *  untouched until Save. A row added via "Add leg" opens straight into edit; cancelling one nobody typed
   *  into drops it. */
  const [editing, setEditing] = useState<{ key: string; draft: TravelRow } | null>(null);

  // ART-240 — the off/travel DAYS linked to this show (per-diem + pay live on the day, not here). The
  // "Travel day" / "Day off" buttons open the day editor pre-linked to this show.
  const days = useTourDays();
  const linkedDays = (days.data?.items ?? []).filter((d) => !!showId && d.showId === showId);
  const [dayModal, setDayModal] =
    useState<{ mode: "edit"; day: TourDayDetail } | { mode: "create"; kind: "off" | "travel" } | null>(null);

  // Seed the local edit buffer from the server ONCE. Keys are minted here (not in render): a render-time map
  // would re-mint every row's key on each keystroke, remounting the inputs (focus/state loss) — adversarial MED.
  useEffect(() => {
    if (rows === null && query.data) setRows((query.data.items ?? []).map(toTravelRow));
  }, [query.data, rows]);

  const current: TravelRow[] = rows ?? [];

  // The one funnel for every row change — add, remove, patch and the parse's append — so dirty is stamped
  // in exactly one place.
  function mutate(fn: (r: TravelRow[]) => TravelRow[]) { setRows((prev) => fn(prev ?? [])); setSaved(false); setDirty(true); }
  function addRow() {
    const row = { kind: "flight", assignedAll: false, personIds: [], needsReimbursement: true, localKey: newTravelKey() } as TravelRow;
    mutate((r) => [...r, row]);
    setEditing({ key: row.localKey, draft: { ...row } });
  }
  function removeRow(k: string) { mutate((r) => r.filter((x) => x.localKey !== k)); setEditing((e) => (e?.key === k ? null : e)); }
  function patch(k: string, p: Partial<TravelRow>) { mutate((r) => r.map((x) => (x.localKey === k ? { ...x, ...p } : x))); }

  // Edit is a discrete MODE (the legacy card): a pencil opens it, the fields write a DRAFT, Save commits the
  // draft over the row and Cancel throws it away — so the compact display row never flickers mid-typing.
  function startEdit(row: TravelRow) { setEditing({ key: row.localKey, draft: { ...row } }); }
  // Editing the draft marks the section dirty (as a keystroke did on the old always-open form), so the
  // footer's fan-out — which saves only dirty sections — commits an open edit even before the row's own Save.
  function patchDraft(p: Partial<TravelRow>) { setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e)); setSaved(false); setDirty(true); }
  function commitEdit() {
    if (!editing) return;
    const d = editing.draft;
    mutate((r) => r.map((x) => (x.localKey === d.localKey ? d : x)));
    setEditing(null);
  }
  function cancelEdit() {
    // A row added and then cancelled without a field typed is dropped: edits live only in the draft, so an
    // empty COMMITTED row is exactly a fresh "Add leg" nobody used.
    if (editing) {
      const committed = current.find((x) => x.localKey === editing.key);
      if (committed && !travelRowHasContent(committed)) removeRow(editing.key);
    }
    setEditing(null);
  }

  async function runParse(body: { imageBase64?: string; mimeType?: string }) {
    setParseFailed(false);
    setSaved(false);
    // NO `if (!showId) return`. The hook posts to the band route when there is no show — same parse, band
    // fence, legs unassigned — so an itinerary can be dropped before the date is typed, which is the order a
    // booking agent actually gets them in. The show-scoped call is still used the moment there IS a show,
    // because only that one can match a passenger to the show's party.
    setParsing(true);
    try {
      const res = await parse.mutateAsync({ showId, body });
      // Functional update — a parse resolving AFTER a manual edit appends to the LATEST rows, not a stale
      // render-time snapshot (adversarial MED).
      mutate((r) => [...r, ...(res.items ?? []).map(parsedToTravelRow)]);
    } catch {
      // 502 (a Gemini outage, or no `Gemini:ApiKey` on this host at all) or any other failure → SAY so. The
      // dropzone stays put; the amber line below names the manual "Add leg" fallback (ART-225 removed the
      // Automatic/Manual toggle and the paste box — a dropped screenshot is the one way in, so there is no
      // mode to send anyone to, only the manual rows).
      setParseFailed(true);
    } finally {
      setParsing(false);
    }
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    // Set BEFORE the file read: `fileToDataUrl` is part of the wait, and the spinner has to cover it.
    setParsing(true);
    try { await runParse(await fileToDataUrl(file)); }
    catch { setParseFailed(true); }
    finally { setParsing(false); }
  }

  /** The travel PUT body — shared by this section's own Save and the footer's fan-out. Folds in an OPEN edit
   *  draft so a Save pressed mid-edit (the footer ribbon, or before the row's own Save) still persists what
   *  is on screen rather than the pre-edit row. */
  function travelBody() {
    const rows = editing ? current.map((x) => (x.localKey === editing.key ? editing.draft : x)) : current;
    return { items: rows.filter(travelRowHasContent).map(travelRowToWire) };
  }

  // ART-241 — publish the live travel draft (OPEN edit-card values overlaid) so section 09 Expenses previews a
  // travel row the instant a cost is typed, before any save. Only for a money:travel seat (the cost is money).
  // The reimbursement setter writes back into THIS section's state — the leg is the flag's one home, and this
  // section's save persists it — so section 09 never writes the flag independently.
  const effectiveTravel = editing ? current.map((x) => (x.localKey === editing.key ? editing.draft : x)) : current;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  useEditorTravel(canTravelMoney ? {
    items: effectiveTravel.map((r) => ({
      localKey: r.localKey,
      travelItemId: r.travelItemId,
      kind: r.kind ?? "flight",
      airline: r.airline,
      flightNumber: r.flightNumber,
      propertyName: r.propertyName,
      costCents: r.cost?.costCents ?? null,
      needsReimbursement: r.needsReimbursement ?? true,
    })),
    setNeedsReimbursement: (localKey, v) => {
      if (editingRef.current?.key === localKey) patchDraft({ needsReimbursement: v });
      else patch(localKey, { needsReimbursement: v });
    },
  } : null);

  // ART-240 — the current per-diem rate (dollars string): the edited value, else the stored rate.
  const perDiemValue = perDiem ?? centsToStr(fin.data?.data?.perDiemCents);

  /** Persist the per-diem RATE through the financials full-replace, carrying the rest of the stored plan so a
   *  per-diem save never blanks a guarantee or a door split. Only when a money seat changed it. */
  async function savePerDiem(id: string) {
    if (!canMoney || !perDiemDirty) return;
    const stored = fin.data?.data;
    await updateFin.mutateAsync({
      showId: id,
      version: fin.data?.version ?? 0,
      body: {
        guaranteeCents: stored?.guaranteeCents ?? null, perPersonCents: stored?.perPersonCents ?? null,
        hotelCents: stored?.hotelCents ?? null, perDiemCents: strToCents(perDiemValue),
        flightsCents: stored?.flightsCents ?? null, gasCents: stored?.gasCents ?? null,
        rentalCents: stored?.rentalCents ?? null, photoVideoCents: stored?.photoVideoCents ?? null,
        fohCents: stored?.fohCents ?? null, memberCount: stored?.memberCount ?? null,
        ticketPriceCents: stored?.ticketPriceCents ?? null,
        agentPct: stored?.agentPct ?? null, managerPct: stored?.managerPct ?? null,
        doorSplit: stored?.doorSplit ?? null, doorSplitPct: stored?.doorSplitPct ?? null,
        doorSplitAfterCents: stored?.doorSplitAfterCents ?? null,
        merchCut: stored?.merchCut ?? null, merchCutPct: stored?.merchCutPct ?? null,
        contractTerms: stored?.contractTerms ?? null, currency: stored?.currency ?? undefined,
      },
    });
    setPerDiemDirty(false);
  }

  function onSave() {
    // No show yet ⇒ nothing to commit against. The footer's Save creates it and flushes this section.
    if (!showId) return;
    setSaved(false);
    save.mutate({ showId, body: travelBody() }, {
      onSuccess: (res) => { setRows((res.items ?? []).map(toTravelRow)); setSaved(true); setDirty(false); setEditing(null); },
    });
    void savePerDiem(showId);
  }

  // 08 Travel was the other section absent from the registry (found alongside ART-85's 09 Financials, and
  // the same defect: inside the modal `SectionSave` renders nothing, so an unregistered section has no Save
  // of its own AND no share of the footer's). Legs typed here were discarded when the editor closed.
  useSectionSave("travel", {
    label: "Travel",
    order: 8,
    isDirty: () => dirty || perDiemDirty,
    save: async (id) => {
      if (dirty) {
        const res = await save.mutateAsync({ showId: id, body: travelBody() });
        setRows((res.items ?? []).map(toTravelRow));
        setDirty(false);
        setEditing(null);
      }
      await savePerDiem(id);
    },
  });

  // The assignment control reads each person's NAME from the same crew source section 03 resolves against.
  const nameOf = useMemberNames();

  if (query.isLoading) return <SectionLoading n={7} label="Travel" />;
  if (query.isError) return <SectionError n={7} label="Travel" query={query} />;

  const memberChips = (members.data?.items ?? []).filter((m) => m.personId);
  const legCount = current.length;
  const countText = `${legCount} ${legCount === 1 ? "item" : "items"}`;

  // ART-240 — the money-gated per-day cost split (per-diem + pay), keyed by day; the run this show is in, so
  // the footnote can name where the run's OTHER days live.
  const costByDay = new Map((linkedDayCosts.data?.days ?? []).map((d) => [d.tourDayId, d]));
  const myRun = (runs.data?.items ?? []).find((r) => (r.shows ?? []).some((s) => s.showId === showId));
  const runOthers = (myRun?.shows ?? []).filter((s) => s.showId !== showId && s.date).map((s) => shortDow(s.date!));

  return (
    <div className={styles.section}>
      <SectionTitle n={7}>Travel</SectionTitle>

      {/* ART-240 — the per-diem RATE (money seat) + the off/travel DAYS linked to THIS show. Only this show's
          days appear here; the rest of a run's days live on the run's other shows. Always shown; a day links to
          a show that exists, so the add buttons wait until this one is saved. */}
      <div className={styles.travelDays}>
        <div className={styles.travelDaysHead}>
          {canMoney && (
            <div className={styles.perDiem}>
              <span className={styles.perDiemLabel}>Per diem</span>
              <div className={styles.perDiemInputRow}>
                <MoneyField label="Per diem rate" hideLabel value={perDiemValue}
                  onChange={(v) => { setPerDiem(v); setPerDiemDirty(true); }} />
                <span className={styles.perDiemCaption}>per member, per day · set for the whole run</span>
              </div>
            </div>
          )}
          <div className={styles.travelDaysActions}>
            <button type="button" className={styles.addDayPill} disabled={!showId}
              title={showId ? undefined : "Create the show first"}
              onClick={() => setDayModal({ mode: "create", kind: "travel" })}>+ Travel day</button>
            <button type="button" className={styles.addDayPill} disabled={!showId}
              title={showId ? undefined : "Create the show first"}
              onClick={() => setDayModal({ mode: "create", kind: "off" })}>+ Day off</button>
          </div>
        </div>

        {linkedDays.map((day) => {
          const cost = costByDay.get(day.tourDayId);
          const headline = day.kind === "travel"
            ? (day.fromCity && day.toCity ? `${day.fromCity} → ${day.toCity}` : day.city || "Travel day")
            : (day.city || "Day off");
          return (
            <div key={day.tourDayId} className={styles.dayCard}>
              <span className={styles.dayDate}>{shortDow(day.date ?? "").toUpperCase()}</span>
              <span className={styles.dayTag}>{day.kind === "travel" ? "TRAVEL" : "OFF"}</span>
              <div className={styles.dayMain}>
                <span className={styles.dayHeadline}>{headline}</span>
                {cost && (
                  <span className={styles.daySub}>
                    Per diem {compactUsd(cost.perDiemCents ?? 0)} · Pay {compactUsd(cost.payCents ?? 0)}
                  </span>
                )}
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => setDayModal({ mode: "edit", day })}>Edit</Button>
            </div>
          );
        })}

        <p className={styles.travelDaysNote}>
          {!showId
            ? "Save the show first to link off/travel days to it."
            : linkedDays.length === 0
              ? "No off/travel days linked to this show yet."
              : <>Only days linked to this show.{runOthers.length > 0 && ` The rest of the run's days live on ${runOthers.join(", ")}.`}</>}
        </p>
      </div>
      {dayModal && (
        <OffDayModal
          existing={dayModal.mode === "edit" ? dayModal.day : null}
          seedShowId={dayModal.mode === "create" ? showId : undefined}
          seedKind={dayModal.mode === "create" ? dayModal.kind : undefined}
          onClose={() => setDayModal(null)}
        />
      )}

      {/* ART-225 (PO, Ryan): the Automatic/Manual toggle and the "…or paste itinerary text" box are gone —
          "EVERYONE will have screenshots", so a dropped flight/hotel confirmation image is the one way in,
          and "Add leg" below is the manual fallback. */}
      {/* ART-93 — a real drop target, which is what the spec draws and what 05 Run of Show already ships. A
          drop is the natural gesture for a PDF or a screenshot sitting in another window. Drawn whether or
          not the show exists — see `runParse`; the parse endpoint is no longer per-show. */}
      <div className={styles.travelParse}>
        <Dropzone
          density="compact"
          tone="bare"
          accept="image/*"
          disabled={parsing}
          label="Travel confirmation"
          prompt={parsing ? "Reading…" : "Drag a file here, or click to browse"}
          // "Save" — the editor has ONE, in the footer ribbon. `SectionSave` renders its child only when a
          // section is mounted with no save registry above it; inside the editor it registers a callback and
          // renders nothing, so the copy says "press Save", not "press Save travel".
          hint={parsing ? "Reading…" : legCount > 0 ? `${countText} added` : "Fills the legs below. Nothing is saved until you press Save."}
          rejectMessage="That doesn't look like an image."
          onFile={(f) => void onFile(f)}
        />
      </div>
      {/* A parse that produces no legs SAYS why — amber, because something broke — directly under the
          dropzone, and it names the manual "Add leg" fallback below. */}
      {parseFailed && (
        <p className={styles.degraded} data-testid="travel-parse-degraded" role="status">
          Couldn’t read that screenshot right now — try again, or add the legs yourself below.
        </p>
      )}

      <ul className={styles.slots}>
        {current.map((row) => {
          // EDIT mode — the legacy card: labelled fields, an "assigned to" dropdown in the head, Cancel/Save.
          if (editing?.key === row.localKey) {
            const d = editing.draft;
            const money = canTravelMoney;
            return (
              <li key={row.localKey} className={styles.travelRow} data-editing="true">
                <div className={styles.editHead}>
                  <span className={styles.editLabel}>Editing</span>
                  {/* The house Picker — same look and keyboard behaviour as the lineup's role control. */}
                  <Picker
                    bare
                    triggerClassName={styles.rolePicker}
                    label="Leg type"
                    value={d.kind ?? "flight"}
                    options={TRAVEL_KINDS.map((k) => ({ value: k.value, label: k.label }))}
                    onChange={(v) => patchDraft({ kind: v })}
                  />
                  <span className={styles.editLabel}>assigned to</span>
                  <AssigneeMenu
                    align="start"
                    value={{ assignedAll: !!d.assignedAll, personIds: d.personIds ?? [] }}
                    members={memberChips}
                    nameOf={nameOf}
                    onChange={(next) => patchDraft(next)}
                  />
                </div>
                {d.kind === "hotel" ? (
                  <div className={styles.editGrid}>
                    <Field label="Property" placeholder="Hotel" value={d.propertyName ?? ""} onChange={(e) => patchDraft({ propertyName: e.target.value })} />
                    {/* The address is what geocodes the hotel for the DOS rideshare/maps deep links — a name
                        alone is not enough. Also shown on the logistics row. */}
                    <Field label="Address" placeholder="Street, city, state" value={d.address ?? ""} onChange={(e) => patchDraft({ address: e.target.value })} />
                    <Field label="Confirmation #" placeholder="Confirmation" value={d.confirmationCode ?? ""} onChange={(e) => patchDraft({ confirmationCode: e.target.value })} />
                    <Field label="Check-in" type="date" value={d.checkIn ?? ""} onChange={(e) => patchDraft({ checkIn: e.target.value || null })} />
                    <Field label="Check-out" type="date" value={d.checkOut ?? ""} onChange={(e) => patchDraft({ checkOut: e.target.value || null })} />
                    <Field label="Room" placeholder="Room" value={d.room ?? ""} onChange={(e) => patchDraft({ room: e.target.value })} />
                    {money && <Field label="Cost (USD)" placeholder="0.00" value={dollars(d.cost?.costCents)} onChange={(e) => patchDraft({ cost: { costCents: parseDollars(e.target.value), currency: d.cost?.currency ?? "USD" } })} />}
                  </div>
                ) : (
                  <div className={styles.editGrid}>
                    <Field label="Airline" placeholder={d.kind === "ground" ? "Provider" : "Airline"} value={d.airline ?? ""} onChange={(e) => patchDraft({ airline: e.target.value })} />
                    <Field label="Flight #" placeholder="Flight #" value={d.flightNumber ?? ""} onChange={(e) => patchDraft({ flightNumber: e.target.value })} />
                    <Field label="Departure" placeholder="From" value={d.departAirport ?? ""} onChange={(e) => patchDraft({ departAirport: e.target.value })} />
                    <Field label="Arrival" placeholder="To" value={d.arriveAirport ?? ""} onChange={(e) => patchDraft({ arriveAirport: e.target.value })} />
                    <Field label="Departure date" type="date" value={localDate(d.departLocal)} onChange={(e) => patchDraft({ departLocal: combineLocal(e.target.value, localTime(d.departLocal)) })} />
                    <Field label="Departure time" type="time" value={localTime(d.departLocal)} onChange={(e) => patchDraft({ departLocal: combineLocal(localDate(d.departLocal), e.target.value) })} />
                    <Field label="Arrival date" type="date" value={localDate(d.arriveLocal)} onChange={(e) => patchDraft({ arriveLocal: combineLocal(e.target.value, localTime(d.arriveLocal)) })} />
                    <Field label="Arrival time" type="time" value={localTime(d.arriveLocal)} onChange={(e) => patchDraft({ arriveLocal: combineLocal(localDate(d.arriveLocal), e.target.value) })} />
                    <Field label="Seat" placeholder="Seat" value={d.seat ?? ""} onChange={(e) => patchDraft({ seat: e.target.value })} />
                    <Field label="Confirmation #" placeholder="Confirmation" value={d.confirmationCode ?? ""} onChange={(e) => patchDraft({ confirmationCode: e.target.value })} />
                    <Field label="Passenger name" placeholder="Passenger" value={d.passengerName ?? ""} onChange={(e) => patchDraft({ passengerName: e.target.value })} />
                    {money && <Field label="Cost (USD)" placeholder="0.00" value={dollars(d.cost?.costCents)} onChange={(e) => patchDraft({ cost: { costCents: parseDollars(e.target.value), currency: d.cost?.currency ?? "USD" } })} />}
                  </div>
                )}
                {/* Visible text stays "Cancel"/"Save" (the legacy card), but the accessible names are scoped
                    to the leg so they never collide with the editor footer's own Cancel/Save. */}
                <div className={styles.editActions}>
                  <Button variant="ghost" aria-label="Cancel leg edit" onClick={cancelEdit}>Cancel</Button>
                  <Button aria-label="Save leg" onClick={commitEdit}>Save</Button>
                </div>
              </li>
            );
          }
          // DISPLAY row — a type tag, one headline + dot-separated sub-line, then the reassign dropdown,
          // an edit pencil and a remove ×.
          const s = travelSummary(row);
          return (
            <li key={row.localKey} className={styles.travelRow}>
              <span className={styles.legTag}>{s.tag}</span>
              <div className={styles.legBody}>
                <div className={styles.legTitle}>{s.title}</div>
                {s.sub && <div className={styles.legSub}>{s.sub}</div>}
              </div>
              <div className={styles.legActions}>
                <AssigneeMenu
                  value={{ assignedAll: !!row.assignedAll, personIds: row.personIds ?? [] }}
                  members={memberChips}
                  nameOf={nameOf}
                  onChange={(next) => patch(row.localKey, next)}
                />
                <IconButton icon={Pencil} label="Edit leg" size="field" onClick={() => startEdit(row)} />
                <IconButton icon={X} label="Remove leg" size="field" onClick={() => removeRow(row.localKey)} />
              </div>
            </li>
          );
        })}
      </ul>
      {/* "Add leg" is the manual fallback and is always present now the Automatic/Manual toggle is gone
          (ART-225). It supersedes ART-89's "no buttons" empty state, which only made sense while a Manual
          mode existed to reach it. `SectionSave` renders nothing inside the editor; the footer ribbon's one
          Save commits this section through the registry. */}
      <div className={styles.actions}>
        <Button variant="ghost" size="sm" onClick={addRow}><Icon icon={Plus} size={16} /> Add leg</Button>
        <SectionSave><Button data-testid="travel-save" onClick={onSave} loading={save.isPending}>Save travel</Button></SectionSave>
        {saved && <span className={styles.savedNote} role="status">Saved</span>}
      </div>
    </div>
  );
}

// ---- The section heading ----------------------------------------------------------------------------------

/**
 * One section's `h3` — a zero-padded ordinal in the ACCENT, then the name in ink.
 *
 * ART-89, *"make the numbers on each section on the scrolling section red"*, and the reference agrees: its
 * `.sec__num` is `--ac-color-accent` beside an ink `.sec__title`. It shipped as one flat `01 · Basics`
 * string repeated at thirteen call sites, so the number could not be styled apart from the name without
 * thirteen edits — which is also why the separator and the padding had thirteen chances to drift.
 *
 * The number is a PROP rather than parsed back out of the label: `"01 · Basics"` is a rendering, and
 * splitting a rendering to recover the parts it was built from is how a display string becomes a data
 * format. `n` is the same ordinal `SECTIONS` carries, so the rail and the heading count from one source.
 */
function SectionTitle({ n, hint, children }: { n: number; hint?: string; children: ReactNode }) {
  return (
    <div className={styles.sectionHeading}>
      <h3 className={styles.sectionTitle}>
      {/* The `{" "}` nodes keep the heading's ACCESSIBLE NAME reading `09 · Financials` — byte-identical to
          the single string this was before the number was split out, so nothing that addresses these headings
          by name has to change its idea of what they are called. They cost nothing visually: a whitespace-only
          anonymous flex item is not rendered (CSS Flexbox §4), and the space you see is the rule's `gap`.
          The separator is deliberately NOT `aria-hidden` for the same reason — it was part of that name
          before, and hiding it would quietly rename ten headings as a side effect of styling one span. */}
        <span className={styles.sectionNum}>{String(n).padStart(2, "0")}</span>{" "}
        <span className={styles.sectionSep}>·</span>{" "}
        {children}
      </h3>
      {/* The section's one-line instruction, BESIDE the heading rather than under it (PO, 2026-09-07). As a
          paragraph below the title it read as the section's first content and pushed the first control down
          a line in every one of the ten. It is a plain string by signature — a hint that can carry markup is
          a hint that grows into a paragraph again, which is the shape this replaced. */}
      {hint != null && <p className={styles.sectionHint}>{hint}</p>}
    </div>
  );
}

// ---- Shared section loading/error shells -----------------------------------------------------------------

function SectionLoading({ n, label }: { n: number; label: string }) {
  return (
    <div className={styles.section}>
      <SectionTitle n={n}>{label}</SectionTitle>
      <div className={styles.center}><Spinner /></div>
    </div>
  );
}

function SectionError({ n, label, query }: { n: number; label: string; query: { error: unknown; refetch: () => unknown } }) {
  return (
    <div className={styles.section}>
      <SectionTitle n={n}>{label}</SectionTitle>
      <EmptyState
        title="Couldn’t load this section"
        description={query.error instanceof ApiError ? query.error.message : "Please try again."}
        action={<Button onClick={() => void query.refetch()}>Retry</Button>}
      />
    </div>
  );
}

// ---- Placeholder sections (08-09) + save-first prompt ----------------------------------------------------

function PlaceholderSection({ label, money }: { label: string; money: boolean }) {
  return (
    <div className={styles.section}>
      <EmptyState
        title={label}
        description={
          money
            ? "Financials and expenses arrive with the money features — this section stays money-free here."
            : "This section arrives in a later update."
        }
      />
    </div>
  );
}


